var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);

// src/wallet/types.ts
var initialWalletState = {
  address: null,
  chainId: null,
  walletType: null,
  status: "disconnected",
  connector: null,
  connectorName: null,
  isSafe: false,
  error: null
};
var defaultWalletTheme = {
  modalBg: "rgba(11, 15, 30, 0.95)",
  cardBg: "rgba(255, 255, 255, 0.04)",
  textPrimary: "#ffffff",
  textSecondary: "rgba(255, 255, 255, 0.6)",
  accentColor: "#6366f1",
  errorColor: "#ef4444",
  successColor: "#22c55e",
  warningColor: "#f59e0b",
  borderColor: "rgba(255, 255, 255, 0.06)",
  borderRadius: "1rem"
};

// src/wallet/adapters/eoa.ts
import {
  connect as wagmiConnect,
  disconnect as wagmiDisconnect,
  switchChain as wagmiSwitchChain,
  sendTransaction as wagmiSendTransaction,
  writeContract as wagmiWriteContract,
  waitForTransactionReceipt,
  signMessage as wagmiSignMessage,
  signTypedData as wagmiSignTypedData,
  getAccount,
  watchAccount,
  getConnectors
} from "@wagmi/core";
var EOAAdapter = class {
  constructor(config) {
    __publicField(this, "type", "eoa");
    __publicField(this, "config");
    __publicField(this, "events", {});
    __publicField(this, "unwatch", null);
    this.config = config;
  }
  // =========================================================================
  // Properties
  // =========================================================================
  get isConnected() {
    const account = getAccount(this.config);
    return account.isConnected;
  }
  get address() {
    const account = getAccount(this.config);
    return account.address ?? null;
  }
  get chainId() {
    const account = getAccount(this.config);
    return account.chainId ?? null;
  }
  // =========================================================================
  // Connection Methods
  // =========================================================================
  async initialize(events) {
    this.events = events;
    this.unwatch = watchAccount(this.config, {
      onChange: (account, prevAccount) => {
        if (account.isConnected && !prevAccount.isConnected) {
          this.events.onConnect?.({
            address: account.address ?? null,
            chainId: account.chainId ?? null,
            walletType: "eoa",
            status: "connected",
            connector: account.connector?.id ?? null,
            connectorName: account.connector?.name ?? null,
            isSafe: false,
            error: null
          });
        } else if (!account.isConnected && prevAccount.isConnected) {
          this.events.onDisconnect?.();
        } else if (account.chainId !== prevAccount.chainId && account.chainId) {
          this.events.onChainChanged?.(account.chainId);
        } else if (account.address !== prevAccount.address && account.address) {
          this.events.onAccountChanged?.(account.address);
        }
      }
    });
  }
  async connect(connectorId) {
    try {
      const connectors = getConnectors(this.config);
      let connector = connectorId ? connectors.find((c) => c.id === connectorId || c.name.toLowerCase().includes(connectorId.toLowerCase())) : connectors[0];
      if (!connector) {
        throw new Error(`Connector not found: ${connectorId}`);
      }
      await wagmiConnect(this.config, { connector });
    } catch (error) {
      this.events.onError?.(error);
      throw error;
    }
  }
  async disconnect() {
    try {
      await wagmiDisconnect(this.config);
    } catch (error) {
      this.events.onError?.(error);
      throw error;
    }
  }
  async switchChain(chainId) {
    try {
      await wagmiSwitchChain(this.config, { chainId });
    } catch (error) {
      const e = error;
      const isUnknownChain = e?.code === 4902 || typeof e?.message === "string" && e.message.toLowerCase().includes("unrecognized chain");
      if (isUnknownChain) {
        const chain = this.config.chains.find((c) => c.id === chainId);
        const provider = typeof window !== "undefined" ? window.ethereum : void 0;
        if (chain && provider?.request) {
          try {
            await provider.request({
              method: "wallet_addEthereumChain",
              params: [
                {
                  chainId: "0x" + chainId.toString(16),
                  chainName: chain.name,
                  nativeCurrency: chain.nativeCurrency,
                  rpcUrls: chain.rpcUrls.default.http,
                  blockExplorerUrls: chain.blockExplorers?.default?.url ? [chain.blockExplorers.default.url] : void 0
                }
              ]
            });
            await wagmiSwitchChain(this.config, { chainId });
            return;
          } catch (addErr) {
            this.events.onError?.(addErr);
            throw addErr;
          }
        }
      }
      this.events.onError?.(error);
      throw error;
    }
  }
  // =========================================================================
  // Transaction Methods
  // =========================================================================
  async sendTransaction(intent) {
    try {
      if (this.chainId !== intent.chainId) {
        await this.switchChain(intent.chainId);
      }
      const hash = await wagmiSendTransaction(this.config, {
        to: intent.to,
        value: intent.value,
        data: intent.data,
        gas: intent.gas
      });
      return {
        hash,
        status: "pending"
      };
    } catch (error) {
      this.events.onError?.(error);
      throw error;
    }
  }
  async writeContract(params) {
    try {
      if (this.chainId !== params.chainId) {
        await this.switchChain(params.chainId);
      }
      const hash = await wagmiWriteContract(this.config, {
        address: params.address,
        abi: params.abi,
        functionName: params.functionName,
        args: params.args,
        value: params.value,
        gas: params.gas
      });
      return {
        hash,
        status: "pending"
      };
    } catch (error) {
      this.events.onError?.(error);
      throw error;
    }
  }
  async waitForTransaction(hash) {
    try {
      const receipt = await waitForTransactionReceipt(this.config, { hash });
      return {
        hash,
        status: receipt.status === "success" ? "confirmed" : "failed",
        receipt
      };
    } catch (error) {
      this.events.onError?.(error);
      return {
        hash,
        status: "failed",
        error: error.message
      };
    }
  }
  // =========================================================================
  // Signing Methods
  // =========================================================================
  async signMessage(message) {
    try {
      return await wagmiSignMessage(this.config, { message });
    } catch (error) {
      this.events.onError?.(error);
      throw error;
    }
  }
  async signTypedData(data) {
    try {
      return await wagmiSignTypedData(this.config, {
        domain: data.domain,
        types: data.types,
        primaryType: data.primaryType,
        message: data.message
      });
    } catch (error) {
      this.events.onError?.(error);
      throw error;
    }
  }
  // =========================================================================
  // Safe Methods (not applicable for EOA)
  // =========================================================================
  isSafeWallet() {
    return false;
  }
  // =========================================================================
  // Cleanup
  // =========================================================================
  destroy() {
    if (this.unwatch) {
      this.unwatch();
      this.unwatch = null;
    }
  }
};
function createEOAAdapter(config) {
  return new EOAAdapter(config);
}

// src/wallet/adapters/safe.ts
import SafeAppsSDK from "@safe-global/safe-apps-sdk";
import { encodeFunctionData } from "viem";
function isInSafeContext() {
  if (typeof window === "undefined") return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}
var SafeAdapter = class {
  constructor() {
    __publicField(this, "type", "safe");
    __publicField(this, "sdk");
    __publicField(this, "events", {});
    __publicField(this, "safeInfo", null);
    __publicField(this, "_isConnected", false);
    this.sdk = new SafeAppsSDK();
  }
  // =========================================================================
  // Properties
  // =========================================================================
  get isConnected() {
    return this._isConnected;
  }
  get address() {
    return this.safeInfo?.address ?? null;
  }
  get chainId() {
    return this.safeInfo?.chainId ?? null;
  }
  // =========================================================================
  // Connection Methods
  // =========================================================================
  async initialize(events) {
    this.events = events;
    if (isInSafeContext()) {
      try {
        await this.connect();
      } catch {
      }
    }
  }
  async connect() {
    try {
      const safeInfo = await this.sdk.safe.getInfo();
      this.safeInfo = {
        address: safeInfo.safeAddress,
        chainId: safeInfo.chainId,
        threshold: safeInfo.threshold,
        owners: safeInfo.owners,
        nonce: safeInfo.nonce
      };
      this._isConnected = true;
      this.events.onConnect?.({
        address: this.safeInfo.address,
        chainId: this.safeInfo.chainId,
        walletType: "safe",
        status: "connected",
        connector: "safe",
        connectorName: "Safe",
        isSafe: true,
        error: null
      });
    } catch (error) {
      this.events.onError?.(error);
      throw new Error("Failed to connect to Safe. Make sure you're running inside the Safe interface.");
    }
  }
  async disconnect() {
    this.safeInfo = null;
    this._isConnected = false;
    this.events.onDisconnect?.();
  }
  async switchChain(_chainId) {
    throw new Error("Safe wallets cannot switch chains. The Safe is tied to a specific network.");
  }
  // =========================================================================
  // Transaction Methods
  // =========================================================================
  async sendTransaction(intent) {
    try {
      if (!this._isConnected || !this.safeInfo) {
        throw new Error("Safe not connected");
      }
      if (intent.chainId !== this.safeInfo.chainId) {
        throw new Error(`Safe is on chain ${this.safeInfo.chainId}, but transaction targets chain ${intent.chainId}`);
      }
      const response = await this.sdk.txs.send({
        txs: [
          {
            to: intent.to,
            value: intent.value?.toString() ?? "0",
            data: intent.data ?? "0x"
          }
        ]
      });
      return {
        hash: response.safeTxHash,
        status: "pending"
      };
    } catch (error) {
      this.events.onError?.(error);
      throw error;
    }
  }
  async writeContract(params) {
    try {
      if (!this._isConnected || !this.safeInfo) {
        throw new Error("Safe not connected");
      }
      if (params.chainId !== this.safeInfo.chainId) {
        throw new Error(`Safe is on chain ${this.safeInfo.chainId}, but transaction targets chain ${params.chainId}`);
      }
      const data = encodeFunctionData({
        abi: params.abi,
        functionName: params.functionName,
        args: params.args
      });
      const response = await this.sdk.txs.send({
        txs: [
          {
            to: params.address,
            value: params.value?.toString() ?? "0",
            data
          }
        ]
      });
      return {
        hash: response.safeTxHash,
        status: "pending"
      };
    } catch (error) {
      this.events.onError?.(error);
      throw error;
    }
  }
  async waitForTransaction(safeTxHash) {
    try {
      const maxAttempts = 60;
      let attempts = 0;
      while (attempts < maxAttempts) {
        const txStatus = await this.sdk.txs.getBySafeTxHash(safeTxHash);
        if (txStatus.txStatus === "SUCCESS") {
          return {
            hash: txStatus.txHash ?? safeTxHash,
            status: "confirmed"
          };
        }
        if (txStatus.txStatus === "FAILED" || txStatus.txStatus === "CANCELLED") {
          return {
            hash: safeTxHash,
            status: "failed",
            error: `Transaction ${txStatus.txStatus.toLowerCase()}`
          };
        }
        await new Promise((resolve) => setTimeout(resolve, 5e3));
        attempts++;
      }
      return {
        hash: safeTxHash,
        status: "pending"
      };
    } catch (error) {
      this.events.onError?.(error);
      return {
        hash: safeTxHash,
        status: "failed",
        error: error.message
      };
    }
  }
  // =========================================================================
  // Signing Methods
  // =========================================================================
  async signMessage(message) {
    try {
      const response = await this.sdk.txs.signMessage(message);
      const sig = response.signature ?? response.safeTxHash;
      if (!sig) {
        throw new Error("No signature returned from Safe");
      }
      return sig;
    } catch (error) {
      this.events.onError?.(error);
      throw error;
    }
  }
  async signTypedData(data) {
    try {
      const response = await this.sdk.txs.signTypedMessage(data);
      const sig = response.signature ?? response.safeTxHash;
      if (!sig) {
        throw new Error("No signature returned from Safe");
      }
      return sig;
    } catch (error) {
      this.events.onError?.(error);
      throw error;
    }
  }
  // =========================================================================
  // Safe-specific Methods
  // =========================================================================
  isSafeWallet() {
    return true;
  }
  async getSafeInfo() {
    if (!this.safeInfo) {
      throw new Error("Safe not connected");
    }
    const info = await this.sdk.safe.getInfo();
    this.safeInfo = {
      address: info.safeAddress,
      chainId: info.chainId,
      threshold: info.threshold,
      owners: info.owners,
      nonce: info.nonce
    };
    return this.safeInfo;
  }
  async getPendingTransactions() {
    return [];
  }
  async confirmTransaction(_safeTxHash) {
    throw new Error("Transaction confirmation is handled through the Safe UI");
  }
  // =========================================================================
  // Cleanup
  // =========================================================================
  destroy() {
    this.safeInfo = null;
    this._isConnected = false;
  }
};
function createSafeAdapter() {
  return new SafeAdapter();
}

// src/wallet/context.ts
import { createContext, useContext } from "react";
var defaultContextValue = {
  wallet: {
    address: null,
    chainId: null,
    walletType: null,
    status: "disconnected",
    connector: null,
    connectorName: null,
    isSafe: false,
    error: null
  },
  adapter: null,
  wagmiConfig: null,
  connect: async () => {
    throw new Error("WalletProvider not found");
  },
  disconnect: async () => {
    throw new Error("WalletProvider not found");
  },
  switchChain: async () => {
    throw new Error("WalletProvider not found");
  },
  isModalOpen: false,
  openModal: () => {
    throw new Error("WalletProvider not found");
  },
  closeModal: () => {
    throw new Error("WalletProvider not found");
  },
  linkedSafeAddress: null,
  setLinkedSafeAddress: () => {
    throw new Error("WalletProvider not found");
  },
  connectors: [],
  pendingTransactions: [],
  addPendingTransaction: () => {
    throw new Error("WalletProvider not found");
  },
  updateTransaction: () => {
    throw new Error("WalletProvider not found");
  },
  removeTransaction: () => {
    throw new Error("WalletProvider not found");
  },
  alerts: [],
  showAlert: () => {
    throw new Error("WalletProvider not found");
  },
  dismissAlert: () => {
    throw new Error("WalletProvider not found");
  },
  clearAlerts: () => {
    throw new Error("WalletProvider not found");
  },
  theme: {
    modalBg: "rgba(11, 15, 30, 0.95)",
    cardBg: "rgba(255, 255, 255, 0.04)",
    textPrimary: "#ffffff",
    textSecondary: "rgba(255, 255, 255, 0.6)",
    accentColor: "#6366f1",
    errorColor: "#ef4444",
    successColor: "#22c55e",
    warningColor: "#f59e0b",
    borderColor: "rgba(255, 255, 255, 0.06)",
    borderRadius: "1rem"
  }
};
var WalletContext = createContext(defaultContextValue);
function useWalletContext() {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error("useWalletContext must be used within a WalletProvider");
  }
  return context;
}

// src/wallet/provider.tsx
import {
  useState,
  useCallback,
  useEffect,
  useMemo
} from "react";
import { WagmiProvider } from "wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// src/wallet/config.ts
import { createConfig, http } from "@wagmi/core";
import { injected, walletConnect } from "@wagmi/connectors";
import {
  mainnet,
  bsc,
  polygon,
  arbitrum,
  base,
  sepolia,
  bscTestnet
} from "viem/chains";
var defaultChains = [
  mainnet,
  bsc,
  polygon,
  arbitrum,
  base,
  sepolia,
  bscTestnet
];
function createWalletConfig(config = {}) {
  const { projectId, appMetadata } = config;
  const connectorsList = [
    injected({ shimDisconnect: true })
  ];
  if (projectId) {
    connectorsList.push(
      walletConnect({
        projectId,
        metadata: appMetadata ? {
          name: appMetadata.name,
          description: appMetadata.description ?? "",
          url: appMetadata.url ?? "",
          icons: appMetadata.icons ?? []
        } : void 0,
        showQrModal: true
      })
    );
  }
  const wagmiConfig = createConfig({
    chains: [mainnet, bsc, polygon, arbitrum, base, sepolia, bscTestnet],
    connectors: connectorsList,
    transports: {
      [mainnet.id]: http(),
      [bsc.id]: http(),
      [polygon.id]: http(),
      [arbitrum.id]: http(),
      [base.id]: http(),
      [sepolia.id]: http(),
      [bscTestnet.id]: http()
    }
  });
  return wagmiConfig;
}
function getChainById(chainId) {
  return defaultChains.find((chain) => chain.id === chainId);
}
function isChainSupported(chainId) {
  return defaultChains.some((chain) => chain.id === chainId);
}

// src/wallet/constants.ts
var WALLET_METADATA = {
  injected: {
    id: "injected",
    name: "Browser Wallet",
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect width="32" height="32" rx="8" fill="#627EEA"/>
      <path d="M16 4L15.82 4.61V20.14L16 20.32L23.5 16L16 4Z" fill="white" fill-opacity="0.6"/>
      <path d="M16 4L8.5 16L16 20.32V12.78V4Z" fill="white"/>
      <path d="M16 21.97L15.9 22.09V27.74L16 28L23.5 17.65L16 21.97Z" fill="white" fill-opacity="0.6"/>
      <path d="M16 28V21.97L8.5 17.65L16 28Z" fill="white"/>
    </svg>`,
    walletType: "eoa",
    description: "Connect using your browser wallet"
  },
  metamask: {
    id: "metamask",
    name: "MetaMask",
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect width="32" height="32" rx="8" fill="#F6851B"/>
      <path d="M24.5 6L17 11.5L18.5 8L24.5 6Z" fill="#E2761B" stroke="#E2761B" stroke-width="0.25"/>
      <path d="M7.5 6L14.9 11.55L13.5 8L7.5 6Z" fill="#E4761B" stroke="#E4761B" stroke-width="0.25"/>
      <path d="M22 20.5L20 24L24 25L25 20.6L22 20.5Z" fill="#E4761B" stroke="#E4761B" stroke-width="0.25"/>
      <path d="M7 20.6L8 25L12 24L10 20.5L7 20.6Z" fill="#E4761B" stroke="#E4761B" stroke-width="0.25"/>
      <path d="M11.8 14.5L10.5 16.5L14.5 16.7L14.3 12.4L11.8 14.5Z" fill="#E4761B" stroke="#E4761B" stroke-width="0.25"/>
      <path d="M20.2 14.5L17.6 12.3L17.5 16.7L21.5 16.5L20.2 14.5Z" fill="#E4761B" stroke="#E4761B" stroke-width="0.25"/>
      <path d="M12 24L14.2 22.9L12.3 20.6L12 24Z" fill="#E4761B" stroke="#E4761B" stroke-width="0.25"/>
      <path d="M17.8 22.9L20 24L19.7 20.6L17.8 22.9Z" fill="#E4761B" stroke="#E4761B" stroke-width="0.25"/>
    </svg>`,
    walletType: "eoa",
    downloadUrl: "https://metamask.io/download/",
    description: "Connect using MetaMask"
  },
  phantom: {
    id: "phantom",
    name: "Phantom",
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect width="32" height="32" rx="8" fill="#AB9FF2"/>
      <path d="M24.5 16.5C24.5 21.5 20.5 25.5 15.5 25.5H9.5C8.4 25.5 7.5 24.6 7.5 23.5V16C7.5 10.5 12 6 17.5 6C23 6 27.5 10.5 27.5 16V16.5H24.5Z" fill="white"/>
      <circle cx="12" cy="15" r="1.5" fill="#AB9FF2"/>
      <circle cx="17" cy="15" r="1.5" fill="#AB9FF2"/>
    </svg>`,
    walletType: "eoa",
    downloadUrl: "https://phantom.app/download",
    description: "Connect using Phantom"
  },
  walletConnect: {
    id: "walletConnect",
    name: "WalletConnect",
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect width="32" height="32" rx="8" fill="#3B99FC"/>
      <path d="M10.5 12.5C14 9 18 9 21.5 12.5L22 13L25 10C20 5 12 5 7 10L10 13L10.5 12.5Z" fill="white"/>
      <path d="M13 15.5L10 12.5L7 15.5L10 18.5L13 15.5Z" fill="white"/>
      <path d="M22 15.5L19 12.5L16 15.5L19 18.5L22 15.5Z" fill="white"/>
      <path d="M16 22L13 19L10 22L13 25L16 22Z" fill="white"/>
      <path d="M22 22L19 19L16 22L19 25L22 22Z" fill="white"/>
    </svg>`,
    walletType: "eoa",
    description: "Scan with WalletConnect"
  },
  safe: {
    id: "safe",
    name: "Safe",
    icon: `
<svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 661.62 661.47">
  <defs>
    <style>
      .cls-1 {
        fill: #fff;
      }
    </style>
  </defs>
  <path class="cls-1" d="M531.98,330.7h-49.42c-14.76,0-26.72,11.96-26.72,26.72v71.73c0,14.76-11.96,26.72-26.72,26.72H232.51c-14.76,0-26.72,11.96-26.72,26.72v49.42c0,14.76,11.96,26.72,26.72,26.72h207.99c14.76,0,26.55-11.96,26.55-26.72v-39.65c0-14.76,11.96-25.23,26.72-25.23h38.2c14.76,0,26.72-11.96,26.72-26.72v-83.3c0-14.76-11.96-26.41-26.72-26.41Z"/>
  <path class="cls-1" d="M205.78,232.52c0-14.76,11.96-26.72,26.72-26.72h196.49c14.76,0,26.72-11.96,26.72-26.72v-49.42c0-14.76-11.96-26.72-26.72-26.72H221.11c-14.76,0-26.72,11.96-26.72,26.72v38.08c0,14.76-11.96,26.72-26.72,26.72h-38.03c-14.76,0-26.72,11.96-26.72,26.72v83.39c0,14.76,12.01,26.12,26.77,26.12h49.42c14.76,0,26.72-11.96,26.72-26.72l-.05-71.44Z"/>
  <path class="cls-1" d="M307.55,278.75h47.47c15.47,0,28.02,12.56,28.02,28.02v47.47c0,15.47-12.56,28.02-28.02,28.02h-47.47c-15.47,0-28.02-12.56-28.02-28.02v-47.47c0-15.47,12.56-28.02,28.02-28.02Z"/>
</svg>`,
    walletType: "safe",
    downloadUrl: "https://app.safe.global/",
    description: "Connect using Safe multi-sig"
  },
  coinbaseWallet: {
    id: "coinbaseWallet",
    name: "Coinbase Wallet",
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect width="32" height="32" rx="8" fill="#0052FF"/>
      <circle cx="16" cy="16" r="10" fill="white"/>
      <rect x="12" y="12" width="8" height="8" rx="2" fill="#0052FF"/>
    </svg>`,
    walletType: "eoa",
    downloadUrl: "https://www.coinbase.com/wallet",
    description: "Connect using Coinbase Wallet"
  }
};
var DEFAULT_CHAINS = {
  mainnet: {
    id: 1,
    name: "Ethereum",
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    rpcUrls: ["https://eth.llamarpc.com"],
    blockExplorers: [{ name: "Etherscan", url: "https://etherscan.io" }]
  },
  bsc: {
    id: 56,
    name: "BNB Smart Chain",
    nativeCurrency: { name: "BNB", symbol: "BNB", decimals: 18 },
    rpcUrls: ["https://bsc-dataseed.binance.org"],
    blockExplorers: [{ name: "BscScan", url: "https://bscscan.com" }]
  },
  polygon: {
    id: 137,
    name: "Polygon",
    nativeCurrency: { name: "MATIC", symbol: "MATIC", decimals: 18 },
    rpcUrls: ["https://polygon-rpc.com"],
    blockExplorers: [{ name: "PolygonScan", url: "https://polygonscan.com" }]
  },
  arbitrum: {
    id: 42161,
    name: "Arbitrum One",
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    rpcUrls: ["https://arb1.arbitrum.io/rpc"],
    blockExplorers: [{ name: "Arbiscan", url: "https://arbiscan.io" }]
  },
  base: {
    id: 8453,
    name: "Base",
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    rpcUrls: ["https://mainnet.base.org"],
    blockExplorers: [{ name: "BaseScan", url: "https://basescan.org" }]
  },
  // Testnets
  sepolia: {
    id: 11155111,
    name: "Sepolia",
    nativeCurrency: { name: "Sepolia Ether", symbol: "ETH", decimals: 18 },
    rpcUrls: ["https://rpc.sepolia.org"],
    blockExplorers: [
      { name: "Etherscan", url: "https://sepolia.etherscan.io" }
    ],
    testnet: true
  },
  bscTestnet: {
    id: 97,
    name: "BSC Testnet",
    nativeCurrency: { name: "tBNB", symbol: "tBNB", decimals: 18 },
    rpcUrls: ["https://data-seed-prebsc-1-s1.binance.org:8545"],
    blockExplorers: [
      { name: "BscScan", url: "https://testnet.bscscan.com" }
    ],
    testnet: true
  }
};
var STORAGE_KEYS = {
  CONNECTED_WALLET: "thema_wallet_connected",
  LAST_CONNECTOR: "thema_wallet_last_connector",
  PREFERRED_CHAIN: "thema_wallet_preferred_chain"
};
var WALLET_EVENTS = {
  CONNECT: "wallet:connect",
  DISCONNECT: "wallet:disconnect",
  CHAIN_CHANGED: "wallet:chainChanged",
  ACCOUNT_CHANGED: "wallet:accountChanged",
  ERROR: "wallet:error",
  TRANSACTION_SENT: "wallet:transactionSent",
  TRANSACTION_CONFIRMED: "wallet:transactionConfirmed",
  TRANSACTION_FAILED: "wallet:transactionFailed"
};

// src/wallet/provider.tsx
import { getConnectors as getConnectors2 } from "@wagmi/core";
import { jsx } from "react/jsx-runtime";
var queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false
    }
  }
});
function WalletProvider({
  children,
  projectId,
  chains: _chains,
  // Reserved for future custom chain config
  defaultChainId: _defaultChainId,
  // Reserved for future use
  theme: themeOverride,
  autoConnect: _autoConnect = true,
  // Reserved for future auto-connect feature
  appMetadata
}) {
  const wagmiConfig = useMemo(
    () => createWalletConfig({ projectId, appMetadata }),
    [projectId, appMetadata]
  );
  const [wallet, setWallet] = useState(initialWalletState);
  const [adapter, setAdapter] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [linkedSafeAddress, setLinkedSafeAddress] = useState(null);
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const theme = useMemo(
    () => ({ ...defaultWalletTheme, ...themeOverride }),
    [themeOverride]
  );
  useEffect(() => {
    const initAdapter = async () => {
      let newAdapter;
      if (isInSafeContext()) {
        newAdapter = createSafeAdapter();
      } else {
        newAdapter = createEOAAdapter(wagmiConfig);
      }
      await newAdapter.initialize({
        onConnect: (state) => {
          setWallet(state);
        },
        onDisconnect: () => {
          setWallet(initialWalletState);
        },
        onChainChanged: (chainId) => {
          setWallet((prev) => ({ ...prev, chainId }));
        },
        onAccountChanged: (address) => {
          setWallet((prev) => ({ ...prev, address }));
        },
        onError: (error) => {
          setWallet((prev) => ({ ...prev, status: "error", error: error.message }));
          showAlert({
            type: "error",
            title: "Wallet Error",
            message: error.message,
            duration: 5e3
          });
        }
      });
      setAdapter(newAdapter);
      if (newAdapter.isConnected) {
        setWallet({
          address: newAdapter.address,
          chainId: newAdapter.chainId,
          walletType: newAdapter.type,
          status: "connected",
          connector: null,
          connectorName: null,
          isSafe: newAdapter.isSafeWallet(),
          error: null
        });
      }
    };
    initAdapter();
    return () => {
      adapter?.destroy();
    };
  }, [wagmiConfig]);
  const connectors = useMemo(() => {
    if (!wagmiConfig) return [];
    const wagmiConnectors = getConnectors2(wagmiConfig);
    return wagmiConnectors.map((connector) => {
      const id = connector.id;
      const metadata = WALLET_METADATA[id] ?? WALLET_METADATA.injected;
      let connectorIcon;
      if (typeof connector.icon === "string") {
        connectorIcon = connector.icon;
      } else if (connector.icon && typeof connector.icon === "object") {
        connectorIcon = connector.icon.dark || connector.icon.light;
      }
      return {
        id,
        name: connector.name || metadata.name,
        icon: connectorIcon || metadata.icon,
        available: true,
        // wagmi only returns available connectors
        connected: wallet.connector === id,
        walletType: metadata.walletType
      };
    });
  }, [wagmiConfig, wallet.connector]);
  const connect = useCallback(
    async (connectorId) => {
      if (!adapter) {
        throw new Error("Wallet not initialized");
      }
      setWallet((prev) => ({ ...prev, status: "connecting" }));
      try {
        await adapter.connect(connectorId);
        setIsModalOpen(false);
      } catch (error) {
        setWallet((prev) => ({
          ...prev,
          status: "error",
          error: error.message
        }));
        throw error;
      }
    },
    [adapter]
  );
  const disconnect = useCallback(async () => {
    if (!adapter) return;
    try {
      await adapter.disconnect();
      setWallet(initialWalletState);
    } catch (error) {
      console.error("Disconnect error:", error);
    }
  }, [adapter]);
  const switchChain = useCallback(
    async (chainId) => {
      if (!adapter) {
        throw new Error("Wallet not initialized");
      }
      await adapter.switchChain(chainId);
    },
    [adapter]
  );
  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);
  const addPendingTransaction = useCallback((tx) => {
    setPendingTransactions((prev) => [...prev, tx]);
  }, []);
  const updateTransaction = useCallback(
    (hash, update) => {
      setPendingTransactions(
        (prev) => prev.map((tx) => tx.hash === hash ? { ...tx, ...update } : tx)
      );
    },
    []
  );
  const removeTransaction = useCallback((hash) => {
    setPendingTransactions((prev) => prev.filter((tx) => tx.hash !== hash));
  }, []);
  const showAlert = useCallback((alert) => {
    setAlerts((prev) => [...prev, alert]);
    if (alert.duration && alert.duration > 0) {
      setTimeout(() => {
        setAlerts((prev) => prev.filter((a) => a !== alert));
      }, alert.duration);
    }
  }, []);
  const dismissAlert = useCallback((index) => {
    setAlerts((prev) => prev.filter((_, i) => i !== index));
  }, []);
  const clearAlerts = useCallback(() => {
    setAlerts([]);
  }, []);
  const contextValue = useMemo(
    () => ({
      wallet,
      adapter,
      wagmiConfig,
      connect,
      disconnect,
      switchChain,
      isModalOpen,
      openModal,
      closeModal,
      linkedSafeAddress,
      setLinkedSafeAddress,
      connectors,
      pendingTransactions,
      addPendingTransaction,
      updateTransaction,
      removeTransaction,
      alerts,
      showAlert,
      dismissAlert,
      clearAlerts,
      theme
    }),
    [
      wallet,
      adapter,
      wagmiConfig,
      connect,
      disconnect,
      switchChain,
      isModalOpen,
      openModal,
      closeModal,
      linkedSafeAddress,
      setLinkedSafeAddress,
      connectors,
      pendingTransactions,
      addPendingTransaction,
      updateTransaction,
      removeTransaction,
      alerts,
      showAlert,
      dismissAlert,
      clearAlerts,
      theme
    ]
  );
  return /* @__PURE__ */ jsx(QueryClientProvider, { client: queryClient, children: /* @__PURE__ */ jsx(WagmiProvider, { config: wagmiConfig, children: /* @__PURE__ */ jsx(WalletContext.Provider, { value: contextValue, children }) }) });
}

// src/hooks/wallet/useWallet.ts
function useWallet() {
  const { wallet, linkedSafeAddress } = useWalletContext();
  const shortAddress = wallet.address ? `${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)}` : null;
  return {
    address: wallet.address,
    chainId: wallet.chainId,
    isConnected: wallet.status === "connected",
    isConnecting: wallet.status === "connecting",
    error: wallet.error,
    walletType: wallet.walletType,
    connector: wallet.connector,
    connectorName: wallet.connectorName,
    isSafe: wallet.isSafe,
    shortAddress,
    linkedSafeAddress
  };
}

// src/hooks/wallet/useConnect.ts
import { useState as useState2, useCallback as useCallback2 } from "react";
function useConnect() {
  const ctx = useWalletContext();
  const [error, setError] = useState2(null);
  const [isConnecting, setIsConnecting] = useState2(false);
  const connect = useCallback2(
    async (connectorId) => {
      setError(null);
      setIsConnecting(true);
      try {
        await ctx.connect(connectorId);
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setIsConnecting(false);
      }
    },
    [ctx.connect]
  );
  const disconnect = useCallback2(async () => {
    setError(null);
    try {
      await ctx.disconnect();
    } catch (e) {
      setError(e);
      throw e;
    }
  }, [ctx.disconnect]);
  return {
    connect,
    disconnect,
    isConnecting,
    error,
    connectors: ctx.connectors,
    openModal: ctx.openModal,
    closeModal: ctx.closeModal,
    isModalOpen: ctx.isModalOpen
  };
}

// src/hooks/wallet/useSafeConnect.ts
import { useCallback as useCallback3, useMemo as useMemo2, useState as useState3 } from "react";

// src/hooks/wallet/useWalletModal.ts
function useWalletModal() {
  const { isModalOpen, openModal, closeModal } = useWalletContext();
  const toggle = () => {
    if (isModalOpen) {
      closeModal();
    } else {
      openModal();
    }
  };
  return {
    isOpen: isModalOpen,
    open: openModal,
    close: closeModal,
    toggle
  };
}

// src/hooks/wallet/useSwitchChain.ts
import { useState as useState4, useCallback as useCallback4 } from "react";
function useSwitchChain() {
  const ctx = useWalletContext();
  const [isSwitching, setIsSwitching] = useState4(false);
  const [error, setError] = useState4(null);
  const chains = defaultChains.map((chain2) => ({
    id: chain2.id,
    name: chain2.name,
    nativeCurrency: chain2.nativeCurrency,
    isTestnet: chain2.testnet ?? false
  }));
  const currentChain = ctx.wallet.chainId ? getChainById(ctx.wallet.chainId) : null;
  const chain = currentChain ? {
    id: currentChain.id,
    name: currentChain.name,
    nativeCurrency: currentChain.nativeCurrency,
    isTestnet: currentChain.testnet ?? false
  } : null;
  const switchChain = useCallback4(
    async (chainId) => {
      if (!isChainSupported(chainId)) {
        throw new Error(`Chain ${chainId} is not supported`);
      }
      setIsSwitching(true);
      setError(null);
      try {
        await ctx.switchChain(chainId);
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setIsSwitching(false);
      }
    },
    [ctx.switchChain]
  );
  return {
    switchChain,
    isSwitching,
    chainId: ctx.wallet.chainId,
    chain,
    chains,
    isSupported: isChainSupported,
    error
  };
}

// src/hooks/wallet/useTransaction.ts
import { useState as useState5, useCallback as useCallback5 } from "react";
function useTransaction() {
  const { adapter, addPendingTransaction, wallet } = useWalletContext();
  const [isPending, setIsPending] = useState5(false);
  const [result, setResult] = useState5(null);
  const [error, setError] = useState5(null);
  const sendTransaction = useCallback5(
    async (intent) => {
      if (!adapter) {
        throw new Error("Wallet not connected");
      }
      setIsPending(true);
      setError(null);
      setResult(null);
      try {
        const txResult = await adapter.sendTransaction(intent);
        addPendingTransaction(txResult);
        setResult(txResult);
        return txResult;
      } catch (e) {
        const err = e;
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    [adapter, addPendingTransaction]
  );
  const reset = useCallback5(() => {
    setIsPending(false);
    setResult(null);
    setError(null);
  }, []);
  return {
    sendTransaction,
    isPending,
    isSigning: isPending && !wallet.isSafe,
    isProposing: isPending && wallet.isSafe,
    result,
    error,
    reset
  };
}

// src/hooks/wallet/useSendTransaction.ts
import { useState as useState6, useCallback as useCallback6 } from "react";
function useSendTransaction() {
  const { adapter, wallet, addPendingTransaction } = useWalletContext();
  const [isPending, setIsPending] = useState6(false);
  const [hash, setHash] = useState6(null);
  const [error, setError] = useState6(null);
  const sendTransaction = useCallback6(
    async (params) => {
      if (!adapter) {
        throw new Error("Wallet not connected");
      }
      if (!wallet.chainId && !params.chainId) {
        throw new Error("No chain ID available");
      }
      setIsPending(true);
      setError(null);
      setHash(null);
      try {
        const result = await adapter.sendTransaction({
          to: params.to,
          value: params.value,
          data: params.data,
          chainId: params.chainId ?? wallet.chainId
        });
        setHash(result.hash);
        addPendingTransaction(result);
        return result;
      } catch (e) {
        const err = e;
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    [adapter, wallet.chainId, addPendingTransaction]
  );
  const reset = useCallback6(() => {
    setIsPending(false);
    setHash(null);
    setError(null);
  }, []);
  return {
    sendTransaction,
    isPending,
    hash,
    error,
    reset
  };
}

// src/hooks/wallet/useWriteContract.ts
import { useState as useState7, useCallback as useCallback7 } from "react";
function useWriteContract() {
  const { adapter, addPendingTransaction } = useWalletContext();
  const [isPending, setIsPending] = useState7(false);
  const [hash, setHash] = useState7(null);
  const [error, setError] = useState7(null);
  const writeContract = useCallback7(
    async (params) => {
      if (!adapter) {
        throw new Error("Wallet not connected");
      }
      setIsPending(true);
      setError(null);
      setHash(null);
      try {
        const result = await adapter.writeContract(params);
        setHash(result.hash);
        addPendingTransaction(result);
        return result;
      } catch (e) {
        const err = e;
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    [adapter, addPendingTransaction]
  );
  const reset = useCallback7(() => {
    setIsPending(false);
    setHash(null);
    setError(null);
  }, []);
  return {
    writeContract,
    isPending,
    hash,
    error,
    reset
  };
}

// src/hooks/wallet/useWaitForTransaction.ts
import { useState as useState8, useCallback as useCallback8, useEffect as useEffect2 } from "react";
function useWaitForTransaction(params = {}) {
  const { hash, enabled = true, onConfirmed, onFailed } = params;
  const { adapter, updateTransaction } = useWalletContext();
  const [isWaiting, setIsWaiting] = useState8(false);
  const [result, setResult] = useState8(null);
  const [error, setError] = useState8(null);
  const waitForTransaction = useCallback8(
    async (txHash) => {
      if (!adapter) {
        throw new Error("Wallet not connected");
      }
      setIsWaiting(true);
      setError(null);
      try {
        const txResult = await adapter.waitForTransaction(txHash);
        updateTransaction(txHash, txResult);
        setResult(txResult);
        if (txResult.status === "confirmed") {
          onConfirmed?.(txResult);
        } else if (txResult.status === "failed") {
          onFailed?.(txResult);
        }
        return txResult;
      } catch (e) {
        const err = e;
        setError(err);
        throw err;
      } finally {
        setIsWaiting(false);
      }
    },
    [adapter, updateTransaction, onConfirmed, onFailed]
  );
  useEffect2(() => {
    if (hash && enabled && !isWaiting && !result) {
      waitForTransaction(hash).catch(() => {
      });
    }
  }, [hash, enabled, isWaiting, result, waitForTransaction]);
  const reset = useCallback8(() => {
    setIsWaiting(false);
    setResult(null);
    setError(null);
  }, []);
  return {
    waitForTransaction,
    isWaiting,
    result,
    error,
    reset
  };
}

// src/hooks/wallet/useSignMessage.ts
import { useState as useState9, useCallback as useCallback9 } from "react";
function useSignMessage() {
  const { adapter } = useWalletContext();
  const [isSigning, setIsSigning] = useState9(false);
  const [signature, setSignature] = useState9(null);
  const [error, setError] = useState9(null);
  const signMessage = useCallback9(
    async (message) => {
      if (!adapter) {
        throw new Error("Wallet not connected");
      }
      setIsSigning(true);
      setError(null);
      setSignature(null);
      try {
        const sig = await adapter.signMessage(message);
        setSignature(sig);
        return sig;
      } catch (e) {
        const err = e;
        setError(err);
        throw err;
      } finally {
        setIsSigning(false);
      }
    },
    [adapter]
  );
  const reset = useCallback9(() => {
    setIsSigning(false);
    setSignature(null);
    setError(null);
  }, []);
  return {
    signMessage,
    isSigning,
    signature,
    error,
    reset
  };
}

// src/hooks/wallet/useSignTypedData.ts
import { useState as useState10, useCallback as useCallback10 } from "react";
function useSignTypedData() {
  const { adapter } = useWalletContext();
  const [isSigning, setIsSigning] = useState10(false);
  const [signature, setSignature] = useState10(null);
  const [error, setError] = useState10(null);
  const signTypedData = useCallback10(
    async (data) => {
      if (!adapter) {
        throw new Error("Wallet not connected");
      }
      setIsSigning(true);
      setError(null);
      setSignature(null);
      try {
        const sig = await adapter.signTypedData(data);
        setSignature(sig);
        return sig;
      } catch (e) {
        const err = e;
        setError(err);
        throw err;
      } finally {
        setIsSigning(false);
      }
    },
    [adapter]
  );
  const reset = useCallback10(() => {
    setIsSigning(false);
    setSignature(null);
    setError(null);
  }, []);
  return {
    signTypedData,
    isSigning,
    signature,
    error,
    reset
  };
}

// src/hooks/wallet/useWalletBalance.ts
import { useState as useState11, useEffect as useEffect3, useCallback as useCallback11 } from "react";
import { getBalance } from "@wagmi/core";
function useWalletBalance() {
  const { wallet, wagmiConfig } = useWalletContext();
  const [balance, setBalance] = useState11(null);
  const [formatted, setFormatted] = useState11(null);
  const [symbol, setSymbol] = useState11(null);
  const [decimals, setDecimals] = useState11(null);
  const [isLoading, setIsLoading] = useState11(false);
  const [error, setError] = useState11(null);
  const fetchBalance = useCallback11(async () => {
    if (!wallet.address || !wallet.chainId || !wagmiConfig) {
      setBalance(null);
      setFormatted(null);
      setSymbol(null);
      setDecimals(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await getBalance(wagmiConfig, {
        address: wallet.address,
        chainId: wallet.chainId
      });
      setBalance(result.value);
      setFormatted(result.formatted);
      setSymbol(result.symbol);
      setDecimals(result.decimals);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [wallet.address, wallet.chainId, wagmiConfig]);
  useEffect3(() => {
    fetchBalance();
  }, [fetchBalance]);
  return {
    balance,
    formatted,
    symbol,
    decimals,
    isLoading,
    error,
    refetch: fetchBalance
  };
}

// src/hooks/wallet/useTokenBalance.ts
import { useState as useState12, useEffect as useEffect4, useCallback as useCallback12 } from "react";
import { readContract } from "@wagmi/core";
import { formatUnits } from "viem";
var ERC20_ABI = [
  {
    inputs: [{ name: "account", type: "address" }],
    name: "balanceOf",
    outputs: [{ name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "decimals",
    outputs: [{ name: "", type: "uint8" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "symbol",
    outputs: [{ name: "", type: "string" }],
    stateMutability: "view",
    type: "function"
  }
];
function useTokenBalance({
  token,
  chainId: overrideChainId,
  account: overrideAccount
}) {
  const { wallet, wagmiConfig } = useWalletContext();
  const account = overrideAccount ?? wallet.address;
  const chainId = overrideChainId ?? wallet.chainId;
  const [balance, setBalance] = useState12(null);
  const [formatted, setFormatted] = useState12(null);
  const [symbol, setSymbol] = useState12(null);
  const [decimals, setDecimals] = useState12(null);
  const [isLoading, setIsLoading] = useState12(false);
  const [error, setError] = useState12(null);
  const fetchBalance = useCallback12(async () => {
    if (!account || !chainId || !wagmiConfig || !token) {
      setBalance(null);
      setFormatted(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const [balanceResult, decimalsResult, symbolResult] = await Promise.all([
        readContract(wagmiConfig, {
          address: token,
          abi: ERC20_ABI,
          functionName: "balanceOf",
          args: [account],
          chainId
        }),
        readContract(wagmiConfig, {
          address: token,
          abi: ERC20_ABI,
          functionName: "decimals",
          chainId
        }),
        readContract(wagmiConfig, {
          address: token,
          abi: ERC20_ABI,
          functionName: "symbol",
          chainId
        })
      ]);
      setBalance(balanceResult);
      setDecimals(decimalsResult);
      setSymbol(symbolResult);
      setFormatted(formatUnits(balanceResult, decimalsResult));
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [account, chainId, wagmiConfig, token]);
  useEffect4(() => {
    fetchBalance();
  }, [fetchBalance]);
  return {
    balance,
    formatted,
    symbol,
    decimals,
    isLoading,
    error,
    refetch: fetchBalance
  };
}

// src/hooks/wallet/useAlerts.ts
import { useCallback as useCallback13 } from "react";
function useAlerts() {
  const { alerts, showAlert, dismissAlert, clearAlerts } = useWalletContext();
  const createAlert = useCallback13(
    (type, title, message, duration = 5e3) => {
      showAlert({ type, title, message, duration });
    },
    [showAlert]
  );
  const showSuccess = useCallback13(
    (title, message) => {
      createAlert("success", title, message);
    },
    [createAlert]
  );
  const showError = useCallback13(
    (title, message) => {
      createAlert("error", title, message);
    },
    [createAlert]
  );
  const showWarning = useCallback13(
    (title, message) => {
      createAlert("warning", title, message);
    },
    [createAlert]
  );
  const showInfo = useCallback13(
    (title, message) => {
      createAlert("info", title, message);
    },
    [createAlert]
  );
  return {
    alerts,
    showAlert,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    dismiss: dismissAlert,
    clearAll: clearAlerts
  };
}

// src/hooks/vault/useConsent.ts
import { useCallback as useCallback15, useEffect as useEffect5, useState as useState13 } from "react";
import { createPublicClient, http as http2 } from "viem";
import {
  ACKNOWLEDGEMENT_STATEMENTS,
  computeAcknowledgementsHash,
  getConsentAcknowledgementsHash,
  hasCurrentConsent,
  getChainConfig
} from "@thema/sdk/ui";

// src/hooks/vault/useVaultWrite.ts
import { useCallback as useCallback14 } from "react";
import { parseUnits } from "viem";
import { VAULT_ABI, ADMIN_ROLE } from "@thema/sdk/ui";
function useVaultWrite({
  vaultAddress,
  chainId,
  assetDecimals = 6,
  accountingDecimals,
  shareDecimals = 18
}) {
  const { writeContract, isPending, hash, error, reset } = useWriteContract();
  const capDecimals = accountingDecimals ?? assetDecimals;
  const chainIdNum = chainId ? parseInt(chainId, 10) : void 0;
  function requireVault() {
    if (!vaultAddress || !chainIdNum) throw new Error("Vault address or chainId not set");
    return { address: vaultAddress, chainId: chainIdNum };
  }
  const deposit = useCallback14(
    async (assets, receiver) => {
      const v = requireVault();
      const result = await writeContract({
        address: v.address,
        abi: VAULT_ABI,
        functionName: "deposit",
        args: [parseUnits(assets, assetDecimals), receiver],
        chainId: v.chainId
      });
      return { hash: result.hash };
    },
    [vaultAddress, chainIdNum, assetDecimals, writeContract]
  );
  const requestRedeem = useCallback14(
    async (shares, controller, owner) => {
      const v = requireVault();
      const result = await writeContract({
        address: v.address,
        abi: VAULT_ABI,
        functionName: "requestRedeem",
        args: [parseUnits(shares, shareDecimals), controller, owner],
        chainId: v.chainId
      });
      return { hash: result.hash };
    },
    [vaultAddress, chainIdNum, shareDecimals, writeContract]
  );
  const claimRedeem = useCallback14(
    async (requestId, receiver) => {
      const v = requireVault();
      const result = await writeContract({
        address: v.address,
        abi: VAULT_ABI,
        functionName: "claimRedeem",
        args: [BigInt(requestId), receiver],
        chainId: v.chainId
      });
      return { hash: result.hash };
    },
    [vaultAddress, chainIdNum, writeContract]
  );
  const settleEpoch = useCallback14(async () => {
    const v = requireVault();
    const result = await writeContract({
      address: v.address,
      abi: VAULT_ABI,
      functionName: "settleEpoch",
      args: [],
      chainId: v.chainId
    });
    return { hash: result.hash };
  }, [vaultAddress, chainIdNum, writeContract]);
  const prepareFinalizeEpoch = useCallback14(
    async (epochId) => {
      const v = requireVault();
      const result = await writeContract({
        address: v.address,
        abi: VAULT_ABI,
        functionName: "prepareFinalizeEpoch",
        args: [BigInt(epochId)],
        chainId: v.chainId
      });
      return { hash: result.hash };
    },
    [vaultAddress, chainIdNum, writeContract]
  );
  const finalizeEpoch = useCallback14(
    async (epochId) => {
      const v = requireVault();
      const result = await writeContract({
        address: v.address,
        abi: VAULT_ABI,
        functionName: "finalizeEpoch",
        args: [BigInt(epochId)],
        chainId: v.chainId
      });
      return { hash: result.hash };
    },
    [vaultAddress, chainIdNum, writeContract]
  );
  const setDepositCap = useCallback14(
    async (newCap) => {
      const v = requireVault();
      const result = await writeContract({
        address: v.address,
        abi: VAULT_ABI,
        functionName: "setDepositCap",
        args: [parseUnits(newCap, capDecimals)],
        chainId: v.chainId
      });
      return { hash: result.hash };
    },
    [vaultAddress, chainIdNum, capDecimals, writeContract]
  );
  const setDefaultWalletCap = useCallback14(
    async (newCap) => {
      const v = requireVault();
      const result = await writeContract({
        address: v.address,
        abi: VAULT_ABI,
        functionName: "setDefaultWalletCap",
        args: [parseUnits(newCap, capDecimals)],
        chainId: v.chainId
      });
      return { hash: result.hash };
    },
    [vaultAddress, chainIdNum, capDecimals, writeContract]
  );
  const setWalletCap = useCallback14(
    async (wallet, newCap) => {
      const v = requireVault();
      const result = await writeContract({
        address: v.address,
        abi: VAULT_ABI,
        functionName: "setWalletCap",
        args: [wallet, parseUnits(newCap, capDecimals)],
        chainId: v.chainId
      });
      return { hash: result.hash };
    },
    [vaultAddress, chainIdNum, capDecimals, writeContract]
  );
  const setWalletCaps = useCallback14(
    async (wallets, newCaps) => {
      if (wallets.length !== newCaps.length) {
        throw new Error("setWalletCaps: wallets and caps arrays must be the same length");
      }
      if (wallets.length === 0) {
        throw new Error("setWalletCaps: at least one (wallet, cap) pair is required");
      }
      const v = requireVault();
      const capsWei = newCaps.map((c) => parseUnits(c, capDecimals));
      const result = await writeContract({
        address: v.address,
        abi: VAULT_ABI,
        functionName: "setWalletCaps",
        args: [wallets, capsWei],
        chainId: v.chainId
      });
      return { hash: result.hash };
    },
    [vaultAddress, chainIdNum, capDecimals, writeContract]
  );
  const clearWalletCap = useCallback14(
    async (wallet) => {
      const v = requireVault();
      const result = await writeContract({
        address: v.address,
        abi: VAULT_ABI,
        functionName: "clearWalletCap",
        args: [wallet],
        chainId: v.chainId
      });
      return { hash: result.hash };
    },
    [vaultAddress, chainIdNum, writeContract]
  );
  const grantAdminRole = useCallback14(
    async (wallet) => {
      const v = requireVault();
      const result = await writeContract({
        address: v.address,
        abi: VAULT_ABI,
        functionName: "grantRole",
        args: [ADMIN_ROLE, wallet],
        chainId: v.chainId
      });
      return { hash: result.hash };
    },
    [vaultAddress, chainIdNum, writeContract]
  );
  const revokeAdminRole = useCallback14(
    async (wallet) => {
      const v = requireVault();
      const result = await writeContract({
        address: v.address,
        abi: VAULT_ABI,
        functionName: "revokeRole",
        args: [ADMIN_ROLE, wallet],
        chainId: v.chainId
      });
      return { hash: result.hash };
    },
    [vaultAddress, chainIdNum, writeContract]
  );
  const setSettler = useCallback14(
    async (newSettler) => {
      const v = requireVault();
      const result = await writeContract({
        address: v.address,
        abi: VAULT_ABI,
        functionName: "setSettler",
        args: [newSettler],
        chainId: v.chainId
      });
      return { hash: result.hash };
    },
    [vaultAddress, chainIdNum, writeContract]
  );
  const sweepExcess = useCallback14(async () => {
    const v = requireVault();
    const result = await writeContract({
      address: v.address,
      abi: VAULT_ABI,
      functionName: "sweepExcess",
      args: [],
      chainId: v.chainId
    });
    return { hash: result.hash };
  }, [vaultAddress, chainIdNum, writeContract]);
  const pause = useCallback14(async () => {
    const v = requireVault();
    const result = await writeContract({
      address: v.address,
      abi: VAULT_ABI,
      functionName: "pause",
      args: [],
      chainId: v.chainId
    });
    return { hash: result.hash };
  }, [vaultAddress, chainIdNum, writeContract]);
  const recordConsent = useCallback14(
    async (expectedAcknowledgementsHash) => {
      const v = requireVault();
      const result = await writeContract({
        address: v.address,
        abi: VAULT_ABI,
        functionName: "recordConsent",
        args: [expectedAcknowledgementsHash],
        chainId: v.chainId
      });
      return { hash: result.hash };
    },
    [vaultAddress, chainIdNum, writeContract]
  );
  const setConsentAcknowledgementsHash = useCallback14(
    async (newHash) => {
      const v = requireVault();
      const result = await writeContract({
        address: v.address,
        abi: VAULT_ABI,
        functionName: "setConsentAcknowledgementsHash",
        args: [newHash],
        chainId: v.chainId
      });
      return { hash: result.hash };
    },
    [vaultAddress, chainIdNum, writeContract]
  );
  const unpause = useCallback14(async () => {
    const v = requireVault();
    const result = await writeContract({
      address: v.address,
      abi: VAULT_ABI,
      functionName: "unpause",
      args: [],
      chainId: v.chainId
    });
    return { hash: result.hash };
  }, [vaultAddress, chainIdNum, writeContract]);
  return {
    deposit,
    requestRedeem,
    claimRedeem,
    settleEpoch,
    prepareFinalizeEpoch,
    finalizeEpoch,
    setDepositCap,
    setDefaultWalletCap,
    setWalletCap,
    setWalletCaps,
    clearWalletCap,
    grantAdminRole,
    revokeAdminRole,
    setSettler,
    sweepExcess,
    pause,
    unpause,
    recordConsent,
    setConsentAcknowledgementsHash,
    isPending,
    hash,
    error,
    reset
  };
}

// src/hooks/vault/useConsent.ts
function useConsent({ vault }) {
  const { address, chainId } = useWallet();
  const { switchChain } = useSwitchChain();
  const vaultChainIdNum = parseInt(vault.chainId, 10);
  const { recordConsent: writeRecordConsent } = useVaultWrite({
    vaultAddress: vault.vaultAddress,
    chainId: vault.chainId
  });
  const [expectedHash, setExpectedHash] = useState13(void 0);
  const [hasConsented, setHasConsented] = useState13(void 0);
  const [status, setStatus] = useState13("idle");
  const [txHash, setTxHash] = useState13(void 0);
  const [error, setError] = useState13(void 0);
  const [isLoading, setIsLoading] = useState13(true);
  const [fetchError, setFetchError] = useState13(void 0);
  const [vaultMissingConsentModule, setVaultMissingConsentModule] = useState13(false);
  const localHash = computeAcknowledgementsHash(ACKNOWLEDGEMENT_STATEMENTS);
  const ZERO_HASH = "0x0000000000000000000000000000000000000000000000000000000000000000";
  const fetchOnchain = useCallback15(async () => {
    setIsLoading(true);
    setFetchError(void 0);
    const { chain } = getChainConfig(vault.chainId);
    const publicClient = createPublicClient({ chain, transport: http2(vault.rpcUrl) });
    try {
      const onchainHash = await getConsentAcknowledgementsHash(publicClient, vault.vaultAddress);
      setExpectedHash(onchainHash);
      setVaultMissingConsentModule(false);
    } catch (e) {
      const err = e;
      const msg = `${err?.name ?? ""} ${err?.message ?? ""}`.toLowerCase();
      const looksLikeMissingSelector = msg.includes("returned no data") || msg.includes("contractfunctionexecutionerror") || msg.includes("reverted") || msg.includes("execution reverted");
      setVaultMissingConsentModule(looksLikeMissingSelector);
      setFetchError(err);
      setExpectedHash(void 0);
      setIsLoading(false);
      return;
    }
    if (address) {
      try {
        const consented = await hasCurrentConsent(publicClient, vault.vaultAddress, address);
        setHasConsented(consented);
      } catch {
        setHasConsented(void 0);
      }
    } else {
      setHasConsented(void 0);
    }
    setIsLoading(false);
  }, [vault.chainId, vault.rpcUrl, vault.vaultAddress, address]);
  useEffect5(() => {
    fetchOnchain().catch((err) => {
      console.error("[useConsent] fetchOnchain failed:", err);
      setFetchError(err);
      setIsLoading(false);
    });
  }, [fetchOnchain]);
  const { result: waitResult, error: waitError } = useWaitForTransaction({
    hash: txHash,
    enabled: !!txHash,
    onConfirmed: () => {
      fetchOnchain().catch(() => {
      });
    }
  });
  useEffect5(() => {
    if (waitResult?.status === "confirmed") {
      setStatus("confirmed");
    } else if (waitResult?.status === "failed") {
      setStatus("error");
      setError(new Error("Transaction failed on-chain"));
    } else if (waitError) {
      setStatus("error");
      setError(waitError);
    }
  }, [waitResult, waitError]);
  const recordConsent = useCallback15(async () => {
    setError(void 0);
    setTxHash(void 0);
    setStatus("preparing");
    if (!address) {
      const err = new Error("Wallet not connected");
      setError(err);
      setStatus("error");
      throw err;
    }
    if (chainId !== vaultChainIdNum) {
      const err = new Error(`Wrong chain \u2014 switch to chainId ${vaultChainIdNum}`);
      setError(err);
      setStatus("error");
      throw err;
    }
    if (!expectedHash || expectedHash === ZERO_HASH) {
      const err = new Error("Consent not initialized on-chain yet");
      setError(err);
      setStatus("error");
      throw err;
    }
    if (expectedHash.toLowerCase() !== localHash.toLowerCase()) {
      const err = new Error("On-chain acknowledgements hash differs from local \u2014 refresh and re-read");
      setError(err);
      setStatus("error");
      throw err;
    }
    setStatus("awaiting-signature");
    try {
      const { hash } = await writeRecordConsent(expectedHash);
      setTxHash(hash);
      setStatus("mining");
      return hash;
    } catch (e) {
      const err = e;
      setError(err);
      setStatus("error");
      throw err;
    }
  }, [address, chainId, vaultChainIdNum, expectedHash, localHash, writeRecordConsent]);
  const switchToVaultChain = useCallback15(async () => {
    await switchChain(vaultChainIdNum);
  }, [switchChain, vaultChainIdNum]);
  let txExplorerUrl;
  if (txHash) {
    try {
      const { chain } = getChainConfig(vault.chainId);
      const base2 = chain.blockExplorers?.default?.url;
      if (base2) txExplorerUrl = `${base2.replace(/\/$/, "")}/tx/${txHash}`;
    } catch {
    }
  }
  const isInitialized = expectedHash !== void 0 ? expectedHash !== ZERO_HASH : void 0;
  const hashesMatch = expectedHash !== void 0 ? isInitialized ? expectedHash.toLowerCase() === localHash.toLowerCase() : false : void 0;
  return {
    expectedHash,
    localHash,
    isInitialized,
    hashesMatch,
    hasConsented,
    isOnCorrectChain: chainId === vaultChainIdNum,
    switchToVaultChain,
    recordConsent,
    status,
    txHash,
    txExplorerUrl,
    error,
    statements: ACKNOWLEDGEMENT_STATEMENTS,
    refetch: fetchOnchain,
    isLoading,
    fetchError,
    vaultMissingConsentModule
  };
}

// src/hooks/vault/useVaultConfig.ts
import { useMemo as useMemo4 } from "react";
import { getChainName } from "@thema/sdk/ui";

// src/hooks/useDeployments.ts
import { useMemo as useMemo3 } from "react";
import {
  listDeployments,
  getDeployment,
  getDeploymentChains,
  getDefaultChain,
  getRpcUrl,
  getOmtDeploymentStartBlock,
  getWrappedTokenNameforChain
} from "@thema/sdk/ui";
function useDeployment(deploymentId) {
  return useMemo3(() => {
    if (!deploymentId) return void 0;
    return getDeployment(deploymentId);
  }, [deploymentId]);
}

// src/hooks/vault/useVaultConfig.ts
function useVaultConfig(deploymentId, vaultRef) {
  const deployment = useDeployment(deploymentId);
  return useMemo4(() => {
    if (!deployment) return void 0;
    const chainId = deployment.defaultChainId;
    const chainDep = deployment.chainDeployments[chainId];
    if (!chainDep) return void 0;
    let pick;
    if (vaultRef) {
      for (const omt of Object.values(chainDep.omts ?? {})) {
        const vaults = omt.vaults ?? {};
        const v = vaults[vaultRef];
        if (v?.address) {
          pick = { vault: v, omtName: omt.name, omtSymbol: omt.symbol };
          break;
        }
      }
    }
    if (!pick) {
      for (const omt of Object.values(chainDep.omts ?? {})) {
        const vaults = omt.vaults ?? {};
        for (const v of Object.values(vaults)) {
          if (v.kind === "cl" && v.address) {
            pick = { vault: v, omtName: omt.name, omtSymbol: omt.symbol };
            break;
          }
        }
        if (pick) break;
      }
    }
    if (!pick) {
      for (const omt of Object.values(chainDep.omts ?? {})) {
        const vaults = omt.vaults ?? {};
        for (const v of Object.values(vaults)) {
          if (v.kind === "lp" && v.address) {
            pick = { vault: v, omtName: omt.name, omtSymbol: omt.symbol };
            break;
          }
        }
        if (pick) break;
      }
    }
    if (!pick) return void 0;
    const { vault, omtName, omtSymbol } = pick;
    let assetSymbol = "";
    let assetDecimals = 0;
    let accountingSymbol = "";
    let accountingDecimals = 0;
    if (vault.kind === "lp") {
      const asset = vault.stablecoin ?? vault.accountingBase;
      assetSymbol = asset?.symbol ?? "";
      assetDecimals = asset?.decimals ?? 0;
      const accounting = vault.accountingBase ?? asset;
      accountingSymbol = accounting?.symbol ?? assetSymbol;
      accountingDecimals = accounting?.decimals ?? assetDecimals;
    } else if (vault.assetSet && vault.assetSet.length > 0) {
      const dominant = [...vault.assetSet].sort((a, b) => b.ratio - a.ratio)[0];
      assetSymbol = dominant.symbol;
      assetDecimals = dominant.decimals;
      accountingSymbol = dominant.symbol;
      accountingDecimals = dominant.decimals;
    }
    const userByName = /* @__PURE__ */ new Map();
    for (const u of deployment.users) {
      if (/^0x[a-fA-F0-9]{40}$/.test(u.address)) {
        userByName.set(u.name, u.address);
      }
    }
    const adminHandles = (vault.kind === "lp" ? vault.roles?.admin ?? [] : []).map((h) => {
      const bare = h.startsWith("@") ? h.slice(1) : h;
      return { handle: h, address: userByName.get(bare) };
    });
    const isAddr = (v) => !!v && /^0x[a-fA-F0-9]{40}$/.test(v);
    const migrationSources = [];
    if (vault.kind === "lp") {
      for (const src of Object.values(vault.migration?.sources ?? {})) {
        if (!isAddr(src.address)) continue;
        migrationSources.push({
          chainId: src.chainId ?? chainId,
          address: src.address,
          exchangeRate: src.exchangeRate
        });
      }
    }
    return {
      omtSymbol,
      omtName: omtName ?? omtSymbol,
      vaultAddress: vault.address,
      chainId,
      chainName: getChainName(chainId),
      rpcUrl: chainDep.rpcUrl,
      assetSymbol,
      assetDecimals,
      accountingSymbol,
      accountingDecimals,
      label: vault.label ?? vault.name ?? `${omtSymbol} LP Vault`,
      cueHandle: vaultRef ?? "",
      enabled: vault.enabled !== false,
      lifecycle: vault.lifecycle ?? "active",
      adminHandles,
      isMigrationTarget: vault.kind === "lp" && (vault.migration?.shareCap ?? 0) > 0,
      migrationSources
    };
  }, [deployment, vaultRef]);
}

// src/hooks/vault/useWalletCapStatus.ts
import { useCallback as useCallback16, useEffect as useEffect6, useRef, useState as useState14 } from "react";
import { createPublicClient as createPublicClient2, http as http3 } from "viem";
import {
  getChainConfig as getChainConfig2,
  getWalletCapStatus
} from "@thema/sdk/ui";
function useWalletCapStatus({
  vault,
  wallet,
  decimals
}) {
  const [status, setStatus] = useState14(null);
  const [isLoading, setIsLoading] = useState14(false);
  const [error, setError] = useState14(null);
  const paramsRef = useRef({ vault, wallet, decimals });
  paramsRef.current = { vault, wallet, decimals };
  const fetch = useCallback16(async () => {
    const { vault: vault2, wallet: wallet2, decimals: decimals2 } = paramsRef.current;
    if (!wallet2 || !vault2 || !decimals2) {
      setStatus(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const { chain } = getChainConfig2(vault2.chainId);
      const publicClient = createPublicClient2({
        chain,
        transport: http3(vault2.rpcUrl)
      });
      const result = await getWalletCapStatus(
        publicClient,
        vault2.vaultAddress,
        wallet2,
        decimals2
      );
      setStatus(result);
    } catch (err) {
      console.error("[useWalletCapStatus] read failed:", err);
      setError(err);
      setStatus(null);
    } finally {
      setIsLoading(false);
    }
  }, []);
  useEffect6(() => {
    fetch();
  }, [
    vault?.vaultAddress,
    vault?.chainId,
    vault?.rpcUrl,
    wallet,
    decimals?.assetDecimals,
    decimals?.accountingDecimals,
    fetch
  ]);
  return { status, isLoading, error, refetch: fetch };
}

// src/hooks/vault/useVaultAssetMetadata.ts
import { useCallback as useCallback17, useEffect as useEffect7, useRef as useRef2, useState as useState15 } from "react";
import { createPublicClient as createPublicClient3, http as http4 } from "viem";
import {
  getChainConfig as getChainConfig3,
  getVaultAssetMetadata
} from "@thema/sdk/ui";
function useVaultAssetMetadata({
  vault
}) {
  const [metadata, setMetadata] = useState15(null);
  const [isLoading, setIsLoading] = useState15(false);
  const [error, setError] = useState15(null);
  const paramsRef = useRef2({ vault });
  paramsRef.current = { vault };
  const fetch = useCallback17(async () => {
    const { vault: vault2 } = paramsRef.current;
    if (!vault2) {
      setMetadata(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const { chain } = getChainConfig3(vault2.chainId);
      const publicClient = createPublicClient3({
        chain,
        transport: http4(vault2.rpcUrl)
      });
      const result = await getVaultAssetMetadata(publicClient, vault2.vaultAddress);
      setMetadata(result);
    } catch (err) {
      console.error("[useVaultAssetMetadata] read failed:", err);
      setError(err);
      setMetadata(null);
    } finally {
      setIsLoading(false);
    }
  }, []);
  useEffect7(() => {
    fetch();
  }, [vault?.vaultAddress, vault?.chainId, vault?.rpcUrl, fetch]);
  return { metadata, isLoading, error, refetch: fetch };
}

// src/hooks/vault/useVaultData.ts
import { useState as useState16, useEffect as useEffect8, useCallback as useCallback18, useRef as useRef3 } from "react";
import { createPublicClient as createPublicClient4, http as http5 } from "viem";
import { getChainConfig as getChainConfig4 } from "@thema/sdk/ui";
import { getVaultState, getUserVaultPosition } from "@thema/sdk/ui";
function useVaultData({
  vault,
  userAddress
}) {
  const [vaultState, setVaultState] = useState16(null);
  const [userPosition, setUserPosition] = useState16(null);
  const [isLoading, setIsLoading] = useState16(false);
  const [error, setError] = useState16(null);
  const paramsRef = useRef3({ vault, userAddress });
  paramsRef.current = { vault, userAddress };
  const fetch = useCallback18(async () => {
    const { vault: vault2, userAddress: userAddress2 } = paramsRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const { chain } = getChainConfig4(vault2.chainId);
      const publicClient = createPublicClient4({
        chain,
        // Use the vault's own RPC — avoids rate limits on public endpoints
        transport: http5(vault2.rpcUrl)
      });
      const [stateResult, positionResult] = await Promise.allSettled([
        getVaultState(publicClient, vault2.vaultAddress, vault2.assetDecimals),
        userAddress2 ? getUserVaultPosition(publicClient, vault2.vaultAddress, userAddress2, vault2.assetDecimals) : Promise.resolve(null)
      ]);
      if (stateResult.status === "fulfilled") {
        setVaultState(stateResult.value);
      } else {
        console.error("[useVaultData] getVaultState failed:", stateResult.reason);
        setError(stateResult.reason);
      }
      if (positionResult.status === "fulfilled") {
        setUserPosition(positionResult.value);
      } else {
        console.warn("[useVaultData] getUserVaultPosition failed (non-fatal):", positionResult.reason);
      }
    } catch (err) {
      console.error("[useVaultData] unexpected error:", err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);
  useEffect8(() => {
    fetch();
  }, [vault.vaultAddress, vault.chainId, vault.rpcUrl, userAddress, fetch]);
  return { vaultState, userPosition, isLoading, error, refetch: fetch };
}

// src/hooks/omt/useTokenApproval.ts
import { useState as useState17, useEffect as useEffect9, useCallback as useCallback19, useMemo as useMemo5 } from "react";
import { readContract as readContract2 } from "@wagmi/core";
import { parseUnits as parseUnits2 } from "viem";
var ERC20_ALLOWANCE_ABI = [
  {
    inputs: [
      { name: "owner", type: "address" },
      { name: "spender", type: "address" }
    ],
    name: "allowance",
    outputs: [{ name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  }
];
var ERC20_APPROVE_ABI = [
  {
    inputs: [
      { name: "spender", type: "address" },
      { name: "amount", type: "uint256" }
    ],
    name: "approve",
    outputs: [{ name: "", type: "bool" }],
    stateMutability: "nonpayable",
    type: "function"
  }
];
function useTokenApproval({
  token,
  owner: overrideOwner,
  spender,
  chainId: overrideChainId,
  requiredAmount,
  requiredWei: overrideRequiredWei,
  decimals = 18
}) {
  const { wallet, wagmiConfig } = useWalletContext();
  const { writeContract, isPending, error: writeError, reset } = useWriteContract();
  const owner = overrideOwner ?? wallet.address;
  const chainId = overrideChainId ?? wallet.chainId;
  const requiredWei = useMemo5(() => {
    if (overrideRequiredWei !== void 0) return overrideRequiredWei;
    if (!requiredAmount) return 0n;
    try {
      return parseUnits2(requiredAmount, decimals);
    } catch {
      return 0n;
    }
  }, [overrideRequiredWei, requiredAmount, decimals]);
  const [allowance, setAllowance] = useState17(null);
  const [isLoading, setIsLoading] = useState17(false);
  const [error, setError] = useState17(null);
  const fetchAllowance = useCallback19(async () => {
    if (!owner || !spender || !token || !wagmiConfig || !chainId) {
      setAllowance(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const allowanceResult = await readContract2(wagmiConfig, {
        address: token,
        abi: ERC20_ALLOWANCE_ABI,
        functionName: "allowance",
        args: [owner, spender],
        chainId
      });
      setAllowance(allowanceResult);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [owner, spender, token, wagmiConfig, chainId]);
  useEffect9(() => {
    fetchAllowance();
  }, [fetchAllowance]);
  const isApproved = requiredWei === 0n ? true : (allowance ?? 0n) >= requiredWei;
  const approve = useCallback19(async () => {
    if (!owner || !spender || !token) {
      throw new Error("Missing approval parameters");
    }
    if (requiredWei === 0n) {
      return { hash: "" };
    }
    reset();
    const result = await writeContract({
      address: token,
      abi: ERC20_APPROVE_ABI,
      functionName: "approve",
      args: [spender, requiredWei],
      chainId
    });
    await fetchAllowance();
    return { hash: result.hash };
  }, [owner, spender, token, requiredWei, writeContract, chainId, reset, fetchAllowance]);
  return {
    allowance,
    requiredWei,
    isApproved,
    isLoading,
    isApproving: isPending,
    error: error || writeError,
    approve,
    refetch: fetchAllowance
  };
}

// src/hooks/vault/usePreviewDeposit.ts
import { useState as useState18, useEffect as useEffect10, useCallback as useCallback20, useRef as useRef4 } from "react";
import { createPublicClient as createPublicClient5, http as http6, parseUnits as parseUnits3, formatUnits as formatUnits2 } from "viem";
import { artifacts, getChainConfig as getChainConfig5 } from "@thema/sdk/ui";
function usePreviewDeposit({
  vault,
  amount,
  assetDecimals,
  shareDecimals,
  debounceMs = 400,
  refreshMs = 1e4,
  paused = false
}) {
  const effectiveShareDecimals = shareDecimals ?? assetDecimals + 6;
  const [debouncedAmount, setDebouncedAmount] = useState18(amount);
  const [shares, setShares] = useState18(null);
  const [isLoading, setIsLoading] = useState18(false);
  const [error, setError] = useState18(null);
  useEffect10(() => {
    const id = setTimeout(() => setDebouncedAmount(amount), debounceMs);
    return () => clearTimeout(id);
  }, [amount, debounceMs]);
  const paramsRef = useRef4({
    vault,
    debouncedAmount,
    assetDecimals,
    effectiveShareDecimals
  });
  paramsRef.current = {
    vault,
    debouncedAmount,
    assetDecimals,
    effectiveShareDecimals
  };
  const genRef = useRef4(0);
  const fetch = useCallback20(async () => {
    const {
      vault: vault2,
      debouncedAmount: debouncedAmount2,
      assetDecimals: assetDecimals2,
      effectiveShareDecimals: effectiveShareDecimals2
    } = paramsRef.current;
    const gen = ++genRef.current;
    const isCurrent = () => gen === genRef.current;
    const num = Number(debouncedAmount2);
    if (!debouncedAmount2 || !Number.isFinite(num) || num <= 0) {
      if (isCurrent()) {
        setShares(null);
        setError(null);
        setIsLoading(false);
      }
      return;
    }
    let assetsWei;
    try {
      assetsWei = parseUnits3(debouncedAmount2, assetDecimals2);
    } catch {
      if (isCurrent()) {
        setShares(null);
        setError(null);
      }
      return;
    }
    if (isCurrent()) {
      setIsLoading(true);
      setError(null);
    }
    try {
      const { chain } = getChainConfig5(vault2.chainId);
      const client = createPublicClient5({
        chain,
        transport: http6(vault2.rpcUrl)
      });
      const sharesWei = await client.readContract({
        address: vault2.vaultAddress,
        abi: artifacts.ThemaLPVault.abi,
        functionName: "previewDeposit",
        args: [assetsWei]
      });
      if (isCurrent()) setShares(formatUnits2(sharesWei, effectiveShareDecimals2));
    } catch (e) {
      if (isCurrent()) {
        setError(e);
        setShares(null);
      }
    } finally {
      if (isCurrent()) setIsLoading(false);
    }
  }, []);
  useEffect10(() => {
    if (paused) {
      genRef.current++;
      return;
    }
    void fetch();
  }, [debouncedAmount, paused, fetch]);
  useEffect10(() => {
    if (paused || !debouncedAmount || refreshMs <= 0) return;
    const id = setInterval(() => void fetch(), refreshMs);
    return () => clearInterval(id);
  }, [debouncedAmount, paused, refreshMs, fetch]);
  return { shares, isLoading, error, refetch: fetch };
}

// src/hooks/vault/waitForVaultReceipt.ts
import { createPublicClient as createPublicClient6, http as http7 } from "viem";
import { getChainConfig as getChainConfig6 } from "@thema/sdk/ui";
async function waitForVaultReceipt(vault, hash, options = {}) {
  const { chain } = getChainConfig6(vault.chainId);
  const client = createPublicClient6({
    chain,
    transport: http7(vault.rpcUrl)
  });
  return client.waitForTransactionReceipt({
    hash,
    timeout: options.timeoutMs ?? 5 * 60 * 1e3
  });
}

// src/hooks/vault/vaultDepositChecks.ts
import { useState as useState19, useEffect as useEffect11 } from "react";
import {
  createPublicClient as createPublicClient7,
  http as http8,
  parseUnits as parseUnits4,
  formatUnits as formatUnits3
} from "viem";
import { artifacts as artifacts2, getChainConfig as getChainConfig7 } from "@thema/sdk/ui";
var VAULT_ABI2 = artifacts2.ThemaLPVault.abi;
function makeClient(vault) {
  const { chain } = getChainConfig7(vault.chainId);
  return createPublicClient7({ chain, transport: http8(vault.rpcUrl) });
}
function useVaultShareDecimals(vault) {
  const [decimals, setDecimals] = useState19(null);
  useEffect11(() => {
    if (!vault) {
      setDecimals(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const d = await makeClient(vault).readContract({
          address: vault.vaultAddress,
          abi: VAULT_ABI2,
          functionName: "decimals"
        });
        if (!cancelled) setDecimals(Number(d));
      } catch (e) {
        console.error("[useVaultShareDecimals] failed to read vault.decimals():", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vault?.vaultAddress, vault?.chainId, vault?.rpcUrl]);
  return decimals;
}
async function preflightDeposit(params) {
  const { vault, account, amount, assetDecimals, assetSymbol, receiver } = params;
  let assetsWei;
  try {
    assetsWei = parseUnits4(amount, assetDecimals);
  } catch (e) {
    return { ok: false, reason: `Invalid amount "${amount}"`, error: e };
  }
  const client = makeClient(vault);
  let maxDeposit;
  try {
    maxDeposit = await client.readContract({
      address: vault.vaultAddress,
      abi: VAULT_ABI2,
      functionName: "maxDeposit",
      args: [receiver]
    });
  } catch (e) {
    return { ok: false, reason: "Could not read deposit cap from vault", error: e };
  }
  if (assetsWei > maxDeposit) {
    return {
      ok: false,
      reason: `Amount exceeds your deposit limit (max ${formatUnits3(maxDeposit, assetDecimals)} ${assetSymbol}).`
    };
  }
  try {
    const result = await client.simulateContract({
      account,
      address: vault.vaultAddress,
      abi: VAULT_ABI2,
      functionName: "deposit",
      args: [assetsWei, receiver]
    });
    return { ok: true, predictedShares: result.result };
  } catch (e) {
    const err = e;
    const reason = err.shortMessage || err.metaMessages && err.metaMessages[0] || err.message || "Deposit would revert on chain";
    return { ok: false, reason, error: e };
  }
}

// src/utils/cn.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/components/wallet/WalletIcon.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var WalletIcon = ({
  wallet,
  size = 32,
  className
}) => {
  const metadata = WALLET_METADATA[wallet] ?? WALLET_METADATA.injected;
  return /* @__PURE__ */ jsx2(
    "div",
    {
      className: cn("flex items-center justify-center", className),
      style: { width: size, height: size },
      dangerouslySetInnerHTML: { __html: metadata.icon }
    }
  );
};

// src/components/wallet/WalletModal.tsx
import { useCallback as useCallback21, useEffect as useEffect13, useMemo as useMemo6, useRef as useRef6, useState as useState20 } from "react";

// src/components/Modal.tsx
import { useEffect as useEffect12, useRef as useRef5 } from "react";
import { createPortal } from "react-dom";
import { IoMdCloseCircleOutline } from "react-icons/io";
import { jsx as jsx3, jsxs } from "react/jsx-runtime";
var Modal = ({
  isOpen,
  onClose,
  title,
  children,
  theme = "dark",
  width = 650,
  className,
  closeOnOverlayClick = true,
  showCloseButton = true
}) => {
  const panelRef = useRef5(null);
  useEffect12(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const panelBase = theme === "dark" ? "bg-[#0F141A] backdrop-blur-xl border border-white/15 text-white" : "bg-white border border-black/10 text-black";
  if (typeof document === "undefined") return null;
  return createPortal(
    /* @__PURE__ */ jsxs("div", { className: "fixed inset-0 z-[1000] flex items-center justify-center", children: [
      /* @__PURE__ */ jsx3(
        "div",
        {
          className: "absolute inset-0 bg-black/60 backdrop-blur-sm",
          onClick: closeOnOverlayClick ? onClose : void 0
        }
      ),
      /* @__PURE__ */ jsxs(
        "div",
        {
          ref: panelRef,
          role: "dialog",
          "aria-modal": "true",
          tabIndex: -1,
          style: { width, maxWidth: "calc(100vw - 32px)" },
          className: cn(
            "relative flex flex-col rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.6)]",
            panelBase,
            className
          ),
          children: [
            /* @__PURE__ */ jsxs(
              "div",
              {
                className: "flex items-center justify-between",
                style: { padding: "20px 28px 12px 28px" },
                children: [
                  title ? /* @__PURE__ */ jsx3("h2", { className: "text-lg font-semibold leading-tight", children: title }) : /* @__PURE__ */ jsx3("span", {}),
                  showCloseButton && /* @__PURE__ */ jsx3(
                    "button",
                    {
                      onClick: onClose,
                      className: "text-white/60 hover:text-white",
                      "aria-label": "Close",
                      style: { marginLeft: 12 },
                      children: /* @__PURE__ */ jsx3(IoMdCloseCircleOutline, { className: "w-5 h-5" })
                    }
                  )
                ]
              }
            ),
            /* @__PURE__ */ jsx3("div", { style: { padding: "8px 28px 24px 28px" }, children })
          ]
        }
      )
    ] }),
    document.body
  );
};

// src/components/wallet/normalizeConnector.ts
function normalizeConnector(connector) {
  const id = connector.id.toLowerCase();
  const name = (connector.name || "").toLowerCase();
  if (id.includes("metamask") || name.includes("metamask")) return "metamask";
  if (id.includes("okx")) return "okx";
  if (id.includes("phantom")) return "phantom";
  if (id.includes("keplr")) return "keplr";
  if (id.includes("coinbase")) return "coinbase";
  if (id.includes("brave")) return "brave";
  if (id.includes("trust")) return "trust";
  if (id.includes("walletconnect")) return "walletconnect";
  return null;
}

// src/components/wallet/walletRegistry.ts
var WALLET_REGISTRY = {
  metamask: { label: "MetaMask", ecosystem: "EVM" },
  okx: { label: "OKX Wallet", ecosystem: "EVM" },
  phantom: { label: "Phantom", ecosystem: "SOLANA" },
  keplr: { label: "Keplr", ecosystem: "COSMOS" },
  coinbase: { label: "Coinbase Wallet", ecosystem: "EVM" },
  trust: { label: "Trust Wallet", ecosystem: "EVM" },
  brave: { label: "Brave Wallet", ecosystem: "EVM" },
  walletconnect: { label: "WalletConnect", ecosystem: "EVM", category: "other" },
  safe: { label: "Safe", ecosystem: "EVM" }
};

// src/components/wallet/WalletModal.tsx
import { jsx as jsx4, jsxs as jsxs2 } from "react/jsx-runtime";
var WalletModal = ({
  theme: themeOverride,
  className,
  title = "Connect your wallet"
}) => {
  const { isModalOpen, closeModal, theme: contextTheme } = useWalletContext();
  const { connect, connectors, isConnecting, error } = useConnect();
  const theme = { ...contextTheme, ...themeOverride };
  const [connectingWallet, setConnectingWallet] = useState20(null);
  const [errorWallet, setErrorWallet] = useState20(null);
  const buttonRefs = useRef6([]);
  useEffect13(() => {
    if (isModalOpen) {
      setConnectingWallet(null);
      setErrorWallet(null);
    }
  }, [isModalOpen]);
  useEffect13(() => {
    if (error && connectingWallet) {
      setErrorWallet(connectingWallet);
      setConnectingWallet(null);
    }
  }, [error, connectingWallet]);
  const handleConnect = useCallback21(
    async (wallet) => {
      if (!wallet.connectorId) {
        if (wallet.installUrl) window.open(wallet.installUrl, "_blank", "noopener,noreferrer");
        return;
      }
      setConnectingWallet(wallet.type);
      setErrorWallet(null);
      try {
        await connect(wallet.connectorId);
      } catch {
      }
    },
    [connect]
  );
  const SUPPORTED = ["metamask", "phantom"];
  const walletEntries = useMemo6(() => {
    const connectorMap = /* @__PURE__ */ new Map();
    connectors.forEach((connector) => {
      const normalized = normalizeConnector(connector);
      if (!normalized) return;
      if (!SUPPORTED.includes(normalized)) return;
      if (!connectorMap.has(normalized)) {
        connectorMap.set(normalized, connector);
      }
    });
    return SUPPORTED.map((type) => {
      const connector = connectorMap.get(type);
      const registry = WALLET_REGISTRY[type];
      return {
        type,
        label: registry.label,
        connectorId: connector?.id,
        icon: connector?.icon ?? walletTypeToFallbackIcon(type),
        installUrl: walletTypeToInstallUrl(type),
        installed: !!connector
      };
    });
  }, [connectors]);
  const orderedWallets = walletEntries;
  const handleKeyDown = useCallback21(
    (event) => {
      if (orderedWallets.length === 0) {
        return;
      }
      const currentIndex = buttonRefs.current.findIndex(
        (btn) => btn === document.activeElement
      );
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const delta = event.key === "ArrowDown" ? 1 : -1;
        const nextIndex = currentIndex === -1 ? 0 : (currentIndex + delta + orderedWallets.length) % orderedWallets.length;
        buttonRefs.current[nextIndex]?.focus();
      }
      if (event.key === "Enter" && currentIndex !== -1) {
        event.preventDefault();
        buttonRefs.current[currentIndex]?.click();
      }
    },
    [orderedWallets.length]
  );
  function walletTypeToFallbackIcon(type) {
    const key = type === "coinbase" ? "coinbaseWallet" : type === "walletconnect" ? "walletConnect" : type;
    const meta = WALLET_METADATA[key];
    return meta?.icon;
  }
  function walletTypeToInstallUrl(type) {
    if (type === "metamask") return "https://metamask.io/download/";
    if (type === "phantom") return "https://phantom.app/download";
    return void 0;
  }
  const renderIcon = (icon) => {
    const safeIcon = icon?.trim() ?? "";
    const isUrl = safeIcon.startsWith("http") || safeIcon.startsWith("data:") || safeIcon.startsWith("/");
    const isInlineSvg = safeIcon.startsWith("<svg");
    if (isUrl) {
      return /* @__PURE__ */ jsx4("img", { src: safeIcon, alt: "", className: "w-10 h-10 object-contain" });
    }
    if (safeIcon && isInlineSvg) {
      return /* @__PURE__ */ jsx4(
        "div",
        {
          className: "w-10 h-10",
          dangerouslySetInnerHTML: { __html: safeIcon }
        }
      );
    }
    return /* @__PURE__ */ jsx4("div", { className: "w-10 h-10 rounded-full bg-white/10" });
  };
  return /* @__PURE__ */ jsx4(
    Modal,
    {
      isOpen: isModalOpen,
      onClose: closeModal,
      title,
      theme: "dark",
      showCloseButton: true,
      width: 440,
      className,
      children: /* @__PURE__ */ jsx4(
        "div",
        {
          className: "flex flex-col",
          style: { gap: 12, paddingTop: 4 },
          onKeyDown: handleKeyDown,
          children: orderedWallets.map((wallet, index) => {
            const isThisConnecting = isConnecting && connectingWallet === wallet.type;
            const isThisError = errorWallet === wallet.type;
            const isDisabled = isConnecting && connectingWallet !== wallet.type;
            let statusEl;
            if (isThisConnecting) {
              statusEl = /* @__PURE__ */ jsx4("span", { className: "w-3.5 h-3.5 border border-current border-t-transparent rounded-full animate-spin inline-block" });
            } else if (isThisError) {
              statusEl = /* @__PURE__ */ jsx4("span", { style: { color: theme.errorColor }, children: "Failed" });
            } else if (!wallet.installed) {
              statusEl = /* @__PURE__ */ jsx4("span", { className: "text-white/40", children: "Install \u2192" });
            } else {
              statusEl = /* @__PURE__ */ jsx4("span", { className: "text-white/40", children: "Connect \u2192" });
            }
            return /* @__PURE__ */ jsxs2(
              "button",
              {
                ref: (node) => {
                  buttonRefs.current[index] = node;
                },
                type: "button",
                onClick: () => !isDisabled && !isThisConnecting && handleConnect(wallet),
                disabled: isDisabled,
                style: { marginLeft: 6, marginRight: 6 },
                className: cn(
                  "flex items-center justify-between gap-3 text-left p-3",
                  "transition-colors duration-150",
                  "hover:bg-white/[0.07] hover:border-white/15",
                  "active:bg-white/[0.10]",
                  isDisabled && "opacity-40 cursor-not-allowed"
                ),
                children: [
                  /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-3 min-w-0", children: [
                    /* @__PURE__ */ jsx4("div", { className: "w-8 h-8 shrink-0 flex items-center justify-center rounded-md overflow-hidden", children: renderIcon(wallet.icon) }),
                    /* @__PURE__ */ jsxs2("div", { className: "flex flex-col min-w-0", children: [
                      /* @__PURE__ */ jsx4(
                        "span",
                        {
                          className: "text-[14px] font-medium leading-tight truncate",
                          style: { color: theme.textPrimary },
                          children: wallet.label
                        }
                      ),
                      !wallet.installed && /* @__PURE__ */ jsx4(
                        "span",
                        {
                          className: "text-[11px] leading-tight mt-0.5",
                          style: { color: theme.textSecondary },
                          children: "Not detected"
                        }
                      )
                    ] })
                  ] }),
                  /* @__PURE__ */ jsx4("div", { className: "text-[11px] uppercase tracking-wide shrink-0", children: statusEl })
                ]
              },
              wallet.type
            );
          })
        }
      )
    }
  );
};

// src/components/wallet/ConnectButton.tsx
import { Fragment, jsx as jsx5, jsxs as jsxs3 } from "react/jsx-runtime";
var ConnectButton = ({
  variant = "primary",
  size = "md",
  showBalance = false,
  showNetwork: _showNetwork = false,
  // Reserved for future use
  theme: themeOverride,
  className,
  connectLabel = "Connect Wallet"
}) => {
  const { isConnected, isConnecting, shortAddress, connector } = useWallet();
  const { openModal, disconnect } = useConnect();
  const { formatted: balance, symbol } = useWalletBalance();
  const { theme: contextTheme } = useWalletContext();
  const theme = { ...contextTheme, ...themeOverride };
  const sizeClasses = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-base"
  };
  const getVariantStyles = () => {
    switch (variant) {
      case "primary":
        return {
          backgroundColor: theme.accentColor,
          color: "#ffffff",
          border: "none"
        };
      case "secondary":
        return {
          backgroundColor: theme.cardBg,
          color: theme.textPrimary,
          border: `1px solid ${theme.borderColor}`
        };
      case "outline":
        return {
          backgroundColor: "transparent",
          color: theme.textPrimary,
          border: `1px solid ${theme.borderColor}`
        };
      case "ghost":
        return {
          backgroundColor: "transparent",
          color: theme.textPrimary,
          border: "none"
        };
    }
  };
  if (isConnected) {
    return /* @__PURE__ */ jsxs3("div", { className: cn("flex items-center gap-2", className), children: [
      showBalance && balance && /* @__PURE__ */ jsxs3(
        "div",
        {
          className: cn(
            "rounded-lg",
            sizeClasses[size]
          ),
          style: {
            backgroundColor: theme.cardBg,
            color: theme.textSecondary,
            border: `1px solid ${theme.borderColor}`
          },
          children: [
            parseFloat(balance).toFixed(4),
            " ",
            symbol
          ]
        }
      ),
      /* @__PURE__ */ jsxs3(
        "button",
        {
          type: "button",
          onClick: disconnect,
          className: cn(
            "flex items-center gap-2 rounded-lg font-medium transition-all",
            "hover:opacity-80",
            sizeClasses[size]
          ),
          style: getVariantStyles(),
          children: [
            connector && /* @__PURE__ */ jsx5(WalletIcon, { wallet: connector, size: 20 }),
            /* @__PURE__ */ jsx5("span", { children: shortAddress })
          ]
        }
      )
    ] });
  }
  return /* @__PURE__ */ jsx5(
    "button",
    {
      type: "button",
      onClick: openModal,
      disabled: isConnecting,
      className: cn(
        "flex items-center gap-2 rounded-lg font-medium transition-all",
        "hover:opacity-80",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        sizeClasses[size],
        className
      ),
      style: getVariantStyles(),
      children: isConnecting ? /* @__PURE__ */ jsxs3(Fragment, { children: [
        /* @__PURE__ */ jsx5(
          "div",
          {
            className: "h-4 w-4 animate-spin rounded-full border-2 border-t-transparent",
            style: { borderColor: "currentColor" }
          }
        ),
        /* @__PURE__ */ jsx5("span", { children: "Connecting..." })
      ] }) : /* @__PURE__ */ jsx5("span", { children: connectLabel })
    }
  );
};

// src/components/wallet/AlertPopup.tsx
import { createPortal as createPortal2 } from "react-dom";
import { jsx as jsx6, jsxs as jsxs4 } from "react/jsx-runtime";
function AlertIcon({ type }) {
  switch (type) {
    case "success":
      return /* @__PURE__ */ jsxs4(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: "20",
          height: "20",
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          children: [
            /* @__PURE__ */ jsx6("path", { d: "M22 11.08V12a10 10 0 1 1-5.93-9.14" }),
            /* @__PURE__ */ jsx6("polyline", { points: "22 4 12 14.01 9 11.01" })
          ]
        }
      );
    case "error":
      return /* @__PURE__ */ jsxs4(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: "20",
          height: "20",
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          children: [
            /* @__PURE__ */ jsx6("circle", { cx: "12", cy: "12", r: "10" }),
            /* @__PURE__ */ jsx6("line", { x1: "15", y1: "9", x2: "9", y2: "15" }),
            /* @__PURE__ */ jsx6("line", { x1: "9", y1: "9", x2: "15", y2: "15" })
          ]
        }
      );
    case "warning":
      return /* @__PURE__ */ jsxs4(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: "20",
          height: "20",
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          children: [
            /* @__PURE__ */ jsx6("path", { d: "m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" }),
            /* @__PURE__ */ jsx6("line", { x1: "12", y1: "9", x2: "12", y2: "13" }),
            /* @__PURE__ */ jsx6("line", { x1: "12", y1: "17", x2: "12.01", y2: "17" })
          ]
        }
      );
    case "info":
    default:
      return /* @__PURE__ */ jsxs4(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: "20",
          height: "20",
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          children: [
            /* @__PURE__ */ jsx6("circle", { cx: "12", cy: "12", r: "10" }),
            /* @__PURE__ */ jsx6("line", { x1: "12", y1: "16", x2: "12", y2: "12" }),
            /* @__PURE__ */ jsx6("line", { x1: "12", y1: "8", x2: "12.01", y2: "8" })
          ]
        }
      );
  }
}
function AlertItem({
  alert,
  index,
  theme,
  onDismiss
}) {
  const getAlertColor = (type) => {
    switch (type) {
      case "success":
        return theme.successColor;
      case "error":
        return theme.errorColor;
      case "warning":
        return theme.warningColor;
      case "info":
      default:
        return theme.accentColor;
    }
  };
  const color = getAlertColor(alert.type);
  return /* @__PURE__ */ jsxs4(
    "div",
    {
      className: cn(
        "flex items-start gap-3 p-4 rounded-xl border backdrop-blur-xl",
        "animate-in slide-in-from-right-5 fade-in duration-300"
      ),
      style: {
        backgroundColor: theme.modalBg,
        borderColor: theme.borderColor,
        borderRadius: theme.borderRadius
      },
      children: [
        /* @__PURE__ */ jsx6("div", { style: { color }, children: AlertIcon({ type: alert.type }) }),
        /* @__PURE__ */ jsxs4("div", { className: "flex-1 min-w-0", children: [
          /* @__PURE__ */ jsx6(
            "div",
            {
              className: "font-medium text-sm",
              style: { color: theme.textPrimary },
              children: alert.title
            }
          ),
          alert.message && /* @__PURE__ */ jsx6(
            "div",
            {
              className: "mt-1 text-sm",
              style: { color: theme.textSecondary },
              children: alert.message
            }
          ),
          alert.action && /* @__PURE__ */ jsx6(
            "button",
            {
              type: "button",
              onClick: alert.action.onClick,
              className: "mt-2 text-sm font-medium hover:underline",
              style: { color },
              children: alert.action.label
            }
          )
        ] }),
        /* @__PURE__ */ jsx6(
          "button",
          {
            type: "button",
            onClick: () => onDismiss(index),
            className: "p-1 rounded-lg hover:bg-white/10 transition-colors",
            style: { color: theme.textSecondary },
            children: /* @__PURE__ */ jsxs4(
              "svg",
              {
                xmlns: "http://www.w3.org/2000/svg",
                width: "16",
                height: "16",
                viewBox: "0 0 24 24",
                fill: "none",
                stroke: "currentColor",
                strokeWidth: "2",
                strokeLinecap: "round",
                strokeLinejoin: "round",
                children: [
                  /* @__PURE__ */ jsx6("path", { d: "M18 6 6 18" }),
                  /* @__PURE__ */ jsx6("path", { d: "m6 6 12 12" })
                ]
              }
            )
          }
        )
      ]
    }
  );
}
var AlertPopup = ({
  theme: themeOverride,
  position = "top-right",
  maxAlerts = 5
}) => {
  const { alerts, dismiss } = useAlerts();
  const { theme: contextTheme, isModalOpen } = useWalletContext();
  const theme = { ...contextTheme, ...themeOverride };
  const positionClasses = {
    "top-right": "top-4 right-4",
    "top-left": "top-4 left-4",
    "bottom-right": "bottom-4 right-4",
    "bottom-left": "bottom-4 left-4",
    "top-center": "top-4 left-1/2 -translate-x-1/2",
    "bottom-center": "bottom-4 left-1/2 -translate-x-1/2"
  };
  if (alerts.length === 0 || typeof document === "undefined" || isModalOpen) {
    return null;
  }
  const visibleAlerts = alerts.slice(0, maxAlerts);
  return createPortal2(
    /* @__PURE__ */ jsx6(
      "div",
      {
        className: cn(
          "fixed z-50 flex flex-col gap-2 w-full max-w-sm",
          positionClasses[position]
        ),
        children: visibleAlerts.map((alert, index) => /* @__PURE__ */ jsx6(
          AlertItem,
          {
            alert,
            index,
            theme,
            onDismiss: dismiss
          },
          index
        ))
      }
    ),
    document.body
  );
};

// src/components/wallet/TransactionStatus.tsx
import { jsx as jsx7, jsxs as jsxs5 } from "react/jsx-runtime";
function StatusIcon({ status, theme }) {
  switch (status) {
    case "pending":
      return /* @__PURE__ */ jsx7(
        "div",
        {
          className: "h-5 w-5 animate-spin rounded-full border-2 border-t-transparent",
          style: { borderColor: theme.warningColor }
        }
      );
    case "confirmed":
      return /* @__PURE__ */ jsxs5(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: "20",
          height: "20",
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: theme.successColor,
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          children: [
            /* @__PURE__ */ jsx7("path", { d: "M22 11.08V12a10 10 0 1 1-5.93-9.14" }),
            /* @__PURE__ */ jsx7("polyline", { points: "22 4 12 14.01 9 11.01" })
          ]
        }
      );
    case "failed":
      return /* @__PURE__ */ jsxs5(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: "20",
          height: "20",
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: theme.errorColor,
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          children: [
            /* @__PURE__ */ jsx7("circle", { cx: "12", cy: "12", r: "10" }),
            /* @__PURE__ */ jsx7("line", { x1: "15", y1: "9", x2: "9", y2: "15" }),
            /* @__PURE__ */ jsx7("line", { x1: "9", y1: "9", x2: "15", y2: "15" })
          ]
        }
      );
  }
}
var TransactionStatus = ({
  hash,
  status,
  isSafe: isSafeProp,
  confirmations,
  threshold,
  theme: themeOverride,
  className,
  showLink = true,
  explorerUrl
}) => {
  const { isSafe: isWalletSafe, chainId } = useWallet();
  const { theme: contextTheme } = useWalletContext();
  const theme = { ...contextTheme, ...themeOverride };
  const isSafe = isSafeProp ?? isWalletSafe;
  const getStatusText = () => {
    if (isSafe && status === "pending") {
      if (confirmations !== void 0 && threshold !== void 0) {
        return `Pending (${confirmations}/${threshold} signatures)`;
      }
      return "Awaiting signatures";
    }
    switch (status) {
      case "pending":
        return "Pending confirmation";
      case "confirmed":
        return "Confirmed";
      case "failed":
        return "Failed";
    }
  };
  const getStatusColor = () => {
    switch (status) {
      case "pending":
        return theme.warningColor;
      case "confirmed":
        return theme.successColor;
      case "failed":
        return theme.errorColor;
    }
  };
  const getExplorerUrl = () => {
    if (explorerUrl) return explorerUrl;
    const explorers = {
      1: "https://etherscan.io",
      56: "https://bscscan.com",
      137: "https://polygonscan.com",
      42161: "https://arbiscan.io",
      8453: "https://basescan.org",
      11155111: "https://sepolia.etherscan.io",
      97: "https://testnet.bscscan.com"
    };
    const base2 = chainId ? explorers[chainId] : null;
    return base2 ? `${base2}/tx/${hash}` : null;
  };
  const shortHash = `${hash.slice(0, 10)}...${hash.slice(-8)}`;
  const explorer = getExplorerUrl();
  return /* @__PURE__ */ jsxs5(
    "div",
    {
      className: cn(
        "flex items-center gap-3 p-3 rounded-lg border",
        className
      ),
      style: {
        backgroundColor: theme.cardBg,
        borderColor: theme.borderColor
      },
      children: [
        /* @__PURE__ */ jsx7(StatusIcon, { status, theme }),
        /* @__PURE__ */ jsxs5("div", { className: "flex-1 min-w-0", children: [
          /* @__PURE__ */ jsx7(
            "div",
            {
              className: "text-sm font-medium",
              style: { color: getStatusColor() },
              children: getStatusText()
            }
          ),
          /* @__PURE__ */ jsx7(
            "div",
            {
              className: "text-xs truncate",
              style: { color: theme.textSecondary },
              children: showLink && explorer ? /* @__PURE__ */ jsx7(
                "a",
                {
                  href: explorer,
                  target: "_blank",
                  rel: "noopener noreferrer",
                  className: "hover:underline",
                  style: { color: theme.accentColor },
                  children: shortHash
                }
              ) : shortHash
            }
          )
        ] }),
        showLink && explorer && /* @__PURE__ */ jsx7(
          "a",
          {
            href: explorer,
            target: "_blank",
            rel: "noopener noreferrer",
            className: "p-1 rounded hover:bg-white/10 transition-colors",
            style: { color: theme.textSecondary },
            children: /* @__PURE__ */ jsxs5(
              "svg",
              {
                xmlns: "http://www.w3.org/2000/svg",
                width: "16",
                height: "16",
                viewBox: "0 0 24 24",
                fill: "none",
                stroke: "currentColor",
                strokeWidth: "2",
                strokeLinecap: "round",
                strokeLinejoin: "round",
                children: [
                  /* @__PURE__ */ jsx7("path", { d: "M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" }),
                  /* @__PURE__ */ jsx7("polyline", { points: "15 3 21 3 21 9" }),
                  /* @__PURE__ */ jsx7("line", { x1: "10", y1: "14", x2: "21", y2: "3" })
                ]
              }
            )
          }
        )
      ]
    }
  );
};
export {
  AlertPopup,
  ConnectButton,
  DEFAULT_CHAINS,
  EOAAdapter,
  STORAGE_KEYS,
  SafeAdapter,
  TransactionStatus,
  WALLET_EVENTS,
  WALLET_METADATA,
  WalletContext,
  WalletIcon,
  WalletModal,
  WalletProvider,
  createEOAAdapter,
  createSafeAdapter,
  createWalletConfig,
  defaultChains,
  defaultWalletTheme,
  getChainById,
  initialWalletState,
  isChainSupported,
  isInSafeContext,
  preflightDeposit,
  useAlerts,
  useConnect,
  useConsent,
  usePreviewDeposit,
  useSendTransaction,
  useSignMessage,
  useSignTypedData,
  useSwitchChain,
  useTokenApproval,
  useTokenBalance,
  useTransaction,
  useVaultAssetMetadata,
  useVaultConfig,
  useVaultData,
  useVaultShareDecimals,
  useVaultWrite,
  useWaitForTransaction,
  useWallet,
  useWalletBalance,
  useWalletCapStatus,
  useWalletContext,
  useWalletModal,
  useWriteContract,
  waitForVaultReceipt
};
//# sourceMappingURL=index.js.map